import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Plus, BarChart3, List, Type, Trash2, LayoutGrid, Pencil, RefreshCw, Tag, PanelLeftClose, PanelLeft, Sparkles, Heading, Image as ImageIcon, TableProperties, Copy, Link2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { GraphConfigModal } from '@/components/reports/graphs/GraphConfigModal';
import { GraphRenderer } from '@/components/reports/graphs/GraphRenderer';
import { useAnalytics } from '@/hooks/useAnalyticsAPI';
import { useHistoricalAnalytics } from '@/hooks/useHistoricalAnalytics';
import { useDiffsAnalytics } from '@/hooks/useDiffsAnalytics';
import { useDiffsCrosstab } from '@/hooks/useDiffsCrosstab';
import type { GraphConfig, GraphElementContent, DiffsAnalyticsRequest, DiffsCrosstabRequest } from '@/components/reports/graphs/types';
import { EVENT_TYPE_CATEGORIES, ALL_EVENT_TYPES } from '@/components/reports/graphs/types';
import { ListConfigModal } from '@/components/reports/lists/ListConfigModal';
import { ListRenderer } from '@/components/reports/lists/ListRenderer';
import { useAnalyticsList } from '@/hooks/useAnalyticsListAPI';
import { useAnalyticsCompanies } from '@/hooks/useAnalyticsCompanies';
import { useAnalyticsPages } from '@/hooks/useAnalyticsPages';
import { useTagsData } from '@/hooks/useTagsData';
import { usePagesSlugs } from '@/hooks/usePagesSlugs';
import type { ListConfig, ListElementContent } from '@/components/reports/lists/types';
import { TileConfigModal } from '@/components/reports/tiles/TileConfigModal';
import { TileRowRenderer } from '@/components/reports/tiles/TileRowRenderer';
import { TileSummaryRenderer } from '@/components/reports/tiles/TileSummaryRenderer';
import type { TileRowConfig, TileRowElementContent } from '@/components/reports/tiles/types';
import { useAnalyticsSummary } from '@/hooks/useAnalyticsSummary';
import { TagsConfigModal } from '@/components/reports/tags/TagsConfigModal';
import { TagsRenderer } from '@/components/reports/tags/TagsRenderer';
import type { TagConfig, TagElementContent } from '@/components/reports/tags/types';
import { ExampleConfigModal } from '@/components/reports/example/ExampleConfigModal';
import { EditTableExampleModal } from '@/components/reports/example/EditTableExampleModal';
import { DEFAULT_ZOOM, type ExampleConfig, type ExampleElementContent, type ExampleTableRow, type ExampleTableConfig, type ExampleTableElementContent } from '@/components/reports/example/types';
import type { SetDefinition } from '@/components/reports/set/types';
import { setDefinitionToFilters, getPeriodForAPI, setDefinitionToAnalyticsRequest } from '@/components/reports/set/setUtils';
import { getRecentQuarters } from '@/utils/chartDataTransformers';
import { createSharedLink, buildComparisonDataFromEvent } from '@/utils/sharedLinksUtils';
import { fetchDiffsPages } from '@/utils/diffsAnalyticsAPI';
import type { ExternalEventAnnotations } from '@/types/externalDiffs';
import { getEventIconUrl } from '@/constants/eventIcons';
import { SavedReportsSidebar } from '@/components/reports/SavedReportsSidebar';
import { SaveReportModal, type SaveReportMode } from '@/components/reports/SaveReportModal';
import { useSavedReport, useCreateSavedReport, useUpdateSavedReport } from '@/hooks/useSavedReports';
import { updateSavedReport } from '@/utils/savedReportsAPI';
import { toast } from 'sonner';
import type { Report, ReportElement, SavedReportWithMeta, ReportVisibility, ElementContextData } from '@/types/savedReports';
import { ReportElementsProvider, useReportElements, useRegisterElementContext } from '@/components/reports/ReportElementsContext';
import { WriteWithAIModal } from '@/components/reports/WriteWithAIModal';
import Markdown from 'markdown-to-jsx';

// Title Element Component
interface TitleElementProps {
  content: string;
  onChange: (content: string) => void;
}

const TitleElement: React.FC<TitleElementProps> = ({ content, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(content);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (value.trim() !== content) {
      onChange(value.trim() || 'My Report Title');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setValue(content);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="w-full flex justify-center">
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="text-4xl font-bold text-gray-900 text-center bg-transparent border-none outline-none resize-none w-full max-w-4xl"
          rows={1}
          style={{
            minHeight: '3rem',
            lineHeight: '1.2'
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1
        onClick={() => setIsEditing(true)}
        className="text-4xl font-bold text-gray-900 text-center cursor-text hover:bg-gray-50 rounded px-4 py-2 transition-colors"
      >
        {content}
      </h1>
    </div>
  );
};

// Element Wrapper with Move Up/Down and Delete
interface ElementWrapperProps {
  id: string;
  index: number;
  totalElements: number;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onInsertBefore: () => void;
  children: React.ReactNode;
  additionalActions?: React.ReactNode; // Additional action buttons to render below move/delete
}

const ElementWrapper: React.FC<ElementWrapperProps> = ({
  id,
  index,
  totalElements,
  onDelete,
  onMoveUp,
  onMoveDown,
  onInsertBefore,
  children,
  additionalActions,
}) => {
  return (
    <div className="group relative pb-4">
      {/* Insert Before Button - shows between elements on hover */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none group-hover:pointer-events-auto">
        <button
          onClick={onInsertBefore}
          className="flex items-center gap-1 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full hover:bg-blue-600 shadow-sm"
          title="Insert element here"
        >
          <Plus className="h-3 w-3" />
          Insert
        </button>
      </div>

      {/* Move Up/Down, Delete Button, and Additional Actions */}
      <div className="absolute -left-12 top-0 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
        <button
          onClick={onMoveUp}
          disabled={index === 0}
          className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Move up"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          onClick={onMoveDown}
          disabled={index === totalElements - 1}
          className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Move down"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
          title="Delete element"
        >
          <Trash2 className="h-4 w-4" />
        </button>
        {additionalActions}
      </div>
      {children}
    </div>
  );
};

// Text Element Component
interface TextElementProps {
  content: string;
  onChange: (content: string) => void;
}

const TextElement: React.FC<TextElementProps> = ({ content, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync value when content prop changes (e.g., loading from saved report)
  useEffect(() => {
    setValue(content);
  }, [content]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
      // Auto-resize
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (value !== content) {
      onChange(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setValue(content);
      setIsEditing(false);
    }
    // Allow Enter for new lines in text element (unlike title which saves on Enter)
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  // Empty state - show placeholder
  if (!content && !isEditing) {
    return (
      <div
        onClick={() => setIsEditing(true)}
        className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4 cursor-text hover:border-gray-300 transition-colors"
      >
        <div className="flex items-center gap-2 text-gray-400">
          <Type className="h-5 w-5" />
          <p className="text-sm">Click to add text...</p>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="w-full bg-white border border-blue-300 rounded-lg p-6 my-4 ring-2 ring-blue-100">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleInput}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-full text-gray-700 bg-transparent border-none outline-none resize-none leading-relaxed"
          placeholder="Enter your text here..."
          rows={1}
          style={{ minHeight: '1.5rem' }}
        />
      </div>
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4 cursor-text hover:border-gray-300 transition-colors"
    >
      <div className="text-gray-700 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_ul]:my-2 [&_ol]:my-2">
        <Markdown options={{ forceBlock: true }}>{content}</Markdown>
      </div>
    </div>
  );
};

// Section Header Element Component - big and bold inline editable header
interface SectionHeaderElementProps {
  content: string;
  onChange: (content: string) => void;
}

const SectionHeaderElement: React.FC<SectionHeaderElementProps> = ({ content, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(content);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(content);
  }, [content]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (value !== content) {
      onChange(value || 'Section Header');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setValue(content);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="my-6">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-full text-2xl font-bold text-gray-900 bg-transparent border-b-2 border-blue-500 outline-none py-2"
          placeholder="Section Header"
        />
      </div>
    );
  }

  return (
    <div className="my-6">
      <h2
        onClick={() => setIsEditing(true)}
        className="text-2xl font-bold text-gray-900 cursor-text hover:bg-gray-50 rounded px-2 -mx-2 py-2 transition-colors"
      >
        {content || 'Section Header'}
      </h2>
    </div>
  );
};

// Helper to strip any Cloudinary transformations and get original image URL
const getOriginalCloudinaryUrl = (url: string): string => {
  if (!url) return url;
  // Cloudinary URLs have format: .../upload/[transformations]/[version]/[path]
  // We want to remove any transformation segments between /upload/ and the version/path
  // Transformations start with letters like c_, w_, h_, f_, q_, etc.
  const match = url.match(/^(.*\/upload\/)((?:[a-z]_[^/]+\/)+)?(.*)$/i);
  if (match) {
    // Return URL without transformation segments
    return match[1] + match[3];
  }
  return url;
};

// Helper to generate Cloudinary URL with crop transformation
const generateCroppedCloudinaryUrl = (
  originalUrl: string,
  cropX: number,
  cropY: number,
  cropWidth: number,
  cropHeight: number,
  zoom: number,
  imageSize: { width: number; height: number }
): string => {
  // Strip existing transformations from URL
  const baseUrl = getOriginalCloudinaryUrl(originalUrl);

  // Parse Cloudinary URL structure
  // Format: https://res.cloudinary.com/{cloud}/image/upload/v{version}/{path}
  const match = baseUrl.match(/(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.+)/);
  if (!match) return originalUrl;

  const [, basePrefix, pathWithVersion] = match;

  // Convert from scaled viewport coordinates to original image coordinates
  // cropX, cropY are in scaled space (pixels * zoom), need to divide by zoom
  let originalCropX = cropX / zoom;
  let originalCropY = cropY / zoom;
  let originalCropWidth = cropWidth / zoom;
  let originalCropHeight = cropHeight / zoom;

  // Handle negative coordinates (image smaller than viewport, centered with padding)
  // Clamp to image bounds
  if (originalCropX < 0) {
    originalCropWidth += originalCropX;  // Reduce width by the negative offset
    originalCropX = 0;
  }
  if (originalCropY < 0) {
    originalCropHeight += originalCropY;  // Reduce height by the negative offset
    originalCropY = 0;
  }

  // Clamp width/height to not exceed image dimensions
  if (originalCropX + originalCropWidth > imageSize.width) {
    originalCropWidth = imageSize.width - originalCropX;
  }
  if (originalCropY + originalCropHeight > imageSize.height) {
    originalCropHeight = imageSize.height - originalCropY;
  }

  // Round to integers
  originalCropX = Math.max(0, Math.round(originalCropX));
  originalCropY = Math.max(0, Math.round(originalCropY));
  originalCropWidth = Math.max(1, Math.round(originalCropWidth));
  originalCropHeight = Math.max(1, Math.round(originalCropHeight));

  // Crop from original image at the exact viewport size
  // This matches what the user sees without any scaling artifacts
  const cropTransform = `c_crop,x_${originalCropX},y_${originalCropY},w_${originalCropWidth},h_${originalCropHeight}/q_auto,f_auto`;

  return `${basePrefix}${cropTransform}/${pathWithVersion}`;
};

// Helper to convert marker from original to cropped coordinates
const markerToCroppedCoordinates = (
  marker: { x: number; y: number } | undefined,
  zoom: number,
  cropX: number,
  cropY: number
): { x: number; y: number } | undefined => {
  if (!marker) return undefined;

  const scaledMarkerX = marker.x * zoom;
  const scaledMarkerY = marker.y * zoom;

  // Position relative to crop region
  return {
    x: Math.round(scaledMarkerX - cropX),
    y: Math.round(scaledMarkerY - cropY)
  };
};

// Helper to calculate viewport crop position from zoom/pan state
const calculateViewportCrop = (
  imageSize: { width: number; height: number },
  viewportSize: { width: number; height: number },
  zoom: number,
  panOffset: { x: number; y: number },
  cropCenter?: { x: number; y: number },
  marker?: { x: number; y: number }
): { x: number; y: number } => {
  const viewWidth = viewportSize.width;
  const viewHeight = viewportSize.height;

  // Apply zoom to image dimensions
  const scaledWidth = imageSize.width * zoom;
  const scaledHeight = imageSize.height * zoom;

  // Max scroll bounds (scaled image larger than viewport)
  const maxScrollX = Math.max(0, scaledWidth - viewWidth);
  const maxScrollY = Math.max(0, scaledHeight - viewHeight);

  // Use cropCenter prop (stable) for image positioning - falls back to marker if not set
  const centerPoint = cropCenter || marker;
  const scaledCenterX = (centerPoint?.x ?? imageSize.width / 2) * zoom;
  const scaledCenterY = (centerPoint?.y ?? imageSize.height / 2) * zoom;

  // Base crop to center on the stable center point
  let baseCropX = Math.max(0, scaledCenterX - viewWidth / 2);
  let baseCropY = Math.max(0, scaledCenterY - viewHeight / 2);

  let cropX: number;
  let cropY: number;

  // Handle horizontal centering/panning independently
  if (scaledWidth <= viewWidth) {
    // Image width fits in viewport - center horizontally, ignore pan offset
    cropX = -(viewWidth - scaledWidth) / 2;
  } else {
    // Image is wider than viewport - apply pan offset scaled by zoom
    const userOffsetX = panOffset.x * zoom;
    cropX = baseCropX - userOffsetX;
    // Clamp to image bounds
    cropX = Math.max(0, Math.min(cropX, maxScrollX));
  }

  // Handle vertical centering/panning independently
  if (scaledHeight <= viewHeight) {
    // Image height fits in viewport - center vertically, ignore pan offset
    cropY = -(viewHeight - scaledHeight) / 2;
  } else {
    // Image is taller than viewport - apply pan offset scaled by zoom
    const userOffsetY = panOffset.y * zoom;
    cropY = baseCropY - userOffsetY;
    // Clamp to image bounds
    cropY = Math.max(0, Math.min(cropY, maxScrollY));
  }

  return { x: cropX, y: cropY };
};

// Process all example elements in a report to add cropped metadata
const processExamplesWithCroppedMetadata = async (elements: ReportElement[]): Promise<ReportElement[]> => {
  // Use stored crop URLs (EXACT URLs from Copy URL button)
  const exampleElements = elements.filter(el => el.type === 'example');
  const startTime = performance.now();

  // Helper to load image dimensions
  const loadImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const startTime = performance.now();
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = (error) => {
        console.error(`❌ [loadImage] Failed to load: ${url.substring(0, 80)}...`, error);
        reject(error);
      };
      img.src = getOriginalCloudinaryUrl(url);
    });
  };

  // Track statistics
  let skippedCount = 0;
  let processedCount = 0;
  let errorCount = 0;

  // Process each element
  const processedElements = await Promise.all(
    elements.map(async (element, index) => {
      // Only process example elements
      if (element.type !== 'example') {
        return element;
      }

      try {
        const content: ExampleElementContent = JSON.parse(element.content);
        const config = content.config;

        // Skip if already processed (has cropped URLs)
        if (config.beforeCroppedUrl && config.afterCroppedUrl) {
          skippedCount++;
          return element;
        }

        // Skip if no stored crop URLs (need user interaction to generate these)
        if (!config.beforeCropUrl || !config.afterCropUrl) {
          skippedCount++;
          return element;
        }

        // Use stored crop URLs directly (EXACT URLs matching Copy URL button)
        const beforeCroppedUrl = config.beforeCropUrl;
        const afterCroppedUrl = config.afterCropUrl;

        // Create enhanced config with cropped versions (just copy the stored URLs)
        const enhancedConfig: ExampleConfig = {
          ...config,
          beforeCroppedUrl,
          afterCroppedUrl
        };

        processedCount++;

        // Return updated element
        return {
          ...element,
          content: JSON.stringify({ config: enhancedConfig })
        };
      } catch (error) {
        console.error(`❌ [processExamples] Failed to process example ${element.id}:`, error);
        errorCount++;
        // Return original element if processing fails
        return element;
      }
    })
  );

  return processedElements;
};

// Example Image Viewer - displays image at configurable zoom centered on cropCenter, with pan support
interface ExampleImageViewerProps {
  imageUrl: string;
  cropCenter?: { x: number; y: number };  // Stable position for image centering (doesn't change during marker drag)
  marker?: { x: number; y: number };      // Marker position (can be moved independently)
  panOffset?: { x: number; y: number };
  zoom: number;
  label: 'Before' | 'After';
  markerColor: string;
  onMarkerChange?: (newMarker: { x: number; y: number }) => void;
  onPanChange?: (newPan: { x: number; y: number }) => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onZoomReset?: () => void;
  onCenter?: () => void;
  onCropUrlChange?: (cropUrl: string) => void;  // Current crop URL (for storing in config)
}

const ExampleImageViewer: React.FC<ExampleImageViewerProps> = ({
  imageUrl: rawImageUrl, cropCenter, marker, panOffset, zoom, label, markerColor, onMarkerChange, onPanChange, onZoomIn, onZoomOut, onZoomReset, onCenter, onCropUrlChange
}) => {
  // Strip any Cloudinary transformations to get original full-size image
  const imageUrl = useMemo(() => getOriginalCloudinaryUrl(rawImageUrl), [rawImageUrl]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  // Local marker position during drag (prevents crop recalculation while dragging)
  const [dragMarker, setDragMarker] = useState<{ x: number; y: number } | null>(null);

  // Lazy load image only when in viewport
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Only load image when visible
          const img = new Image();
          img.onload = () => setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
          img.src = imageUrl;
          observer.disconnect(); // Stop observing after loading
        }
      },
      { rootMargin: '200px' } // Start loading 200px before entering viewport
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [imageUrl]);

  // Track container size
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Calculate crop position and marker viewport position with zoom
  // Uses same approach as AnnotationEditor - shows portion of scaled image centered on cropCenter
  // IMPORTANT: cropCenter is STABLE (from prop), marker position can move independently
  const { cropX, cropY, markerViewX, markerViewY, scaledWidth, scaledHeight, imageFitsInViewport } = useMemo(() => {
    if (!imageSize || containerSize.width === 0) {
      return { cropX: 0, cropY: 0, markerViewX: 0, markerViewY: 0, scaledWidth: 0, scaledHeight: 0, imageFitsInViewport: true };
    }

    const viewWidth = containerSize.width;
    const viewHeight = containerSize.height;

    // Apply zoom to image dimensions
    const scaledWidth = imageSize.width * zoom;
    const scaledHeight = imageSize.height * zoom;

    // Check if image fits entirely in viewport
    const imageFitsInViewport = scaledWidth <= viewWidth && scaledHeight <= viewHeight;

    // Max scroll bounds (scaled image larger than viewport)
    const maxScrollX = Math.max(0, scaledWidth - viewWidth);
    const maxScrollY = Math.max(0, scaledHeight - viewHeight);

    // Use cropCenter prop (stable) for image positioning - falls back to marker if not set
    const centerPoint = cropCenter || marker;
    const scaledCenterX = (centerPoint?.x ?? imageSize.width / 2) * zoom;
    const scaledCenterY = (centerPoint?.y ?? imageSize.height / 2) * zoom;

    // Base crop to center on the stable center point
    let baseCropX = Math.max(0, scaledCenterX - viewWidth / 2);
    let baseCropY = Math.max(0, scaledCenterY - viewHeight / 2);

    let cropX: number;
    let cropY: number;

    // Handle horizontal centering/panning independently
    if (scaledWidth <= viewWidth) {
      // Image width fits in viewport - center horizontally, ignore pan offset
      cropX = -(viewWidth - scaledWidth) / 2;
    } else {
      // Image is wider than viewport - apply pan offset scaled by zoom
      // Pan offset is stored in viewport pixels at zoom 1.0, so scale it by current zoom
      const userOffsetX = (panOffset?.x || 0) * zoom;
      cropX = baseCropX - userOffsetX;
      // Clamp to image bounds
      cropX = Math.max(0, Math.min(cropX, maxScrollX));
    }

    // Handle vertical centering/panning independently
    if (scaledHeight <= viewHeight) {
      // Image height fits in viewport - center vertically, ignore pan offset
      cropY = -(viewHeight - scaledHeight) / 2;
    } else {
      // Image is taller than viewport - apply pan offset scaled by zoom
      // Pan offset is stored in viewport pixels at zoom 1.0, so scale it by current zoom
      const userOffsetY = (panOffset?.y || 0) * zoom;
      cropY = baseCropY - userOffsetY;
      // Clamp to image bounds
      cropY = Math.max(0, Math.min(cropY, maxScrollY));
    }

    // For marker display, use dragMarker during drag, otherwise marker prop
    const displayMarker = dragMarker || marker;
    const markerViewX = displayMarker ? displayMarker.x * zoom - cropX : 0;
    const markerViewY = displayMarker ? displayMarker.y * zoom - cropY : 0;

    return { cropX, cropY, markerViewX, markerViewY, scaledWidth, scaledHeight, imageFitsInViewport };
  }, [imageSize, containerSize, cropCenter, marker, panOffset, zoom, dragMarker]);

  // Store callback in ref to avoid effect re-running when callback changes
  const onCropUrlChangeRef = useRef(onCropUrlChange);
  onCropUrlChangeRef.current = onCropUrlChange;

  // Generate and report current crop URL whenever viewport state changes
  useEffect(() => {
    if (imageSize && containerSize.width > 0 && onCropUrlChangeRef.current) {
      const cropUrl = generateCroppedCloudinaryUrl(
        rawImageUrl,
        cropX,
        cropY,
        containerSize.width,
        containerSize.height,
        zoom,
        imageSize
      );
      onCropUrlChangeRef.current(cropUrl);
    }
  }, [imageSize, containerSize, cropX, cropY, zoom, rawImageUrl]);

  // Handle marker drag - use local dragMarker state during drag for smooth movement
  const handleMarkerMouseDown = (e: React.MouseEvent) => {
    if (!onMarkerChange) return;
    e.preventDefault();
    e.stopPropagation();
    // Initialize drag marker to current position
    setDragMarker(marker || null);
    setIsDraggingMarker(true);
  };

  // Handle pan start (clicking on image, not marker)
  const handleContainerMouseDown = (e: React.MouseEvent) => {
    if (!onPanChange) return;
    // Don't allow panning when image fits entirely in viewport
    if (imageFitsInViewport) return;
    // Don't start panning if clicking on marker or already dragging marker
    if ((e.target as HTMLElement).closest('.example-marker')) return;
    if (isDraggingMarker) return;
    e.preventDefault();
    setIsPanning(true);
    setPanStart({
      x: e.clientX,
      y: e.clientY,
      offsetX: panOffset?.x || 0,
      offsetY: panOffset?.y || 0
    });
  };

  // Store callbacks in refs to avoid effect re-running
  const onMarkerChangeRef = useRef(onMarkerChange);
  onMarkerChangeRef.current = onMarkerChange;
  const dragMarkerRef = useRef(dragMarker);
  dragMarkerRef.current = dragMarker;

  // Marker drag effect - update local dragMarker during drag, commit to config on mouseup
  useEffect(() => {
    if (!isDraggingMarker || !containerRef.current || !imageSize) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = containerRef.current!.getBoundingClientRect();
      const viewportX = e.clientX - rect.left;
      const viewportY = e.clientY - rect.top;

      // Convert viewport position to image pixel coordinates (add crop offset, then divide by zoom)
      const imageX = (viewportX + cropX) / zoom;
      const imageY = (viewportY + cropY) / zoom;

      // Clamp to image bounds
      const clampedX = Math.max(0, Math.min(imageSize.width, imageX));
      const clampedY = Math.max(0, Math.min(imageSize.height, imageY));

      // Update local drag state only (keeps image stable)
      const newMarker = { x: Math.round(clampedX), y: Math.round(clampedY) };
      setDragMarker(newMarker);
      dragMarkerRef.current = newMarker;
    };

    const handleMouseUp = () => {
      // Commit the drag marker position to config
      if (dragMarkerRef.current && onMarkerChangeRef.current) {
        onMarkerChangeRef.current(dragMarkerRef.current);
      }
      // Clear drag state
      setIsDraggingMarker(false);
      setDragMarker(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingMarker, imageSize, cropX, cropY, zoom]);

  // Pan drag effect - use ref to avoid effect re-running on onPanChange change
  const onPanChangeRef = useRef(onPanChange);
  onPanChangeRef.current = onPanChange;

  useEffect(() => {
    // Don't pan while marker is being dragged
    if (!isPanning || isDraggingMarker) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      // Apply 5x speed multiplier to make panning faster (less dragging required)
      const PAN_SPEED = 5;
      const newOffsetX = panStart.offsetX + (dx * PAN_SPEED);
      const newOffsetY = panStart.offsetY + (dy * PAN_SPEED);
      onPanChangeRef.current?.({ x: newOffsetX, y: newOffsetY });
    };

    const handleMouseUp = () => setIsPanning(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, isDraggingMarker, panStart]);

  return (
    <div className="flex-1 min-w-[300px]">
      {/* Header with label and zoom controls */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
          <span className={`w-2 h-2 rounded-full ${markerColor}`} />
          {label}
          {/* Debug: Copy Cropped URL */}
          {imageSize && containerSize.width > 0 && (
            <button
              onClick={() => {
                // Use actual container dimensions - what you see is what you get
                const croppedUrl = generateCroppedCloudinaryUrl(
                  rawImageUrl,
                  cropX,
                  cropY,
                  containerSize.width,
                  containerSize.height,
                  zoom,
                  imageSize
                );
                navigator.clipboard.writeText(croppedUrl);
                toast.success(`${label} cropped URL copied (${Math.round(containerSize.width)}×${Math.round(containerSize.height)})`);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title={`Copy ${label} cropped URL to clipboard`}
            >
              <Copy className="h-3 w-3" />
            </button>
          )}
        </div>
        {onZoomIn && onZoomOut && onZoomReset && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400 mr-1">{Math.round(zoom * 100)}%</span>
            <button
              onClick={onZoomOut}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Zoom out"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
            </button>
            <button
              onClick={onZoomIn}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Zoom in"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
              </svg>
            </button>
            <button
              onClick={onZoomReset}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Reset zoom"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            {onCenter && (
              <button
                onClick={onCenter}
                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                title="Center image"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
      <div
        ref={containerRef}
        className={`relative w-full h-[300px] bg-gray-100 rounded-lg overflow-hidden ${onPanChange && !imageFitsInViewport ? 'cursor-grab active:cursor-grabbing' : ''}`}
        onMouseDown={handleContainerMouseDown}
      >
        {imageSize && scaledWidth > 0 && (
          <>
            {/* Image container - transform moves scaled image to show crop region */}
            <div
              className="absolute select-none"
              style={{
                width: scaledWidth,
                height: scaledHeight,
                transform: `translate(${-cropX}px, ${-cropY}px)`,
              }}
            >
              <img
                src={imageUrl}
                alt={label}
                className="block w-full h-full pointer-events-none"
                draggable={false}
                loading="lazy"
              />
            </div>
            {/* Marker overlay - draggable */}
            {(dragMarker || marker) && (
              <div
                className={`example-marker absolute flex items-center z-10 ${onMarkerChange ? 'cursor-grab active:cursor-grabbing' : 'pointer-events-none'} ${isDraggingMarker ? 'scale-110' : ''}`}
                style={{
                  left: Math.max(8, Math.min(containerSize.width - 60, markerViewX - 20)),
                  top: Math.max(8, Math.min(containerSize.height - 32, markerViewY - 12)),
                  transition: isDraggingMarker ? 'none' : 'transform 0.1s ease-out',
                }}
                onMouseDown={handleMarkerMouseDown}
              >
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-white shadow-lg ${isDraggingMarker ? 'ring-2 ring-blue-400' : ''}`}
                  style={{ backgroundColor: '#aebb36' }}
                >
                  <span>{'\u2192'}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Example Element Component
interface ExampleElementProps {
  elementId: string;
  content: string;
  onContentChange?: (content: string) => void;
}

const ExampleElement: React.FC<ExampleElementProps> = ({ elementId, content, onContentChange }) => {
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [captionValue, setCaptionValue] = useState('');
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerValue, setHeaderValue] = useState('');

  const exampleContent: ExampleElementContent | null = useMemo(() => {
    try {
      if (!content) return null;
      return JSON.parse(content) as ExampleElementContent;
    } catch {
      return null;
    }
  }, [content]);

  const config = exampleContent?.config;
  // Independent zoom for each image
  const beforeZoom = config?.beforeZoom ?? DEFAULT_ZOOM;
  const afterZoom = config?.afterZoom ?? DEFAULT_ZOOM;

  // Marker fallback logic: if one image doesn't have a marker, use the other's for pan
  const effectiveBeforeCropCenter = config?.beforeCropCenter || config?.beforeMarker || config?.afterCropCenter || config?.afterMarker;
  const effectiveAfterCropCenter = config?.afterCropCenter || config?.afterMarker || config?.beforeCropCenter || config?.beforeMarker;

  // Sync caption value with config
  useEffect(() => {
    setCaptionValue(config?.caption || '');
  }, [config?.caption]);

  // Sync header value with config
  useEffect(() => {
    setHeaderValue(config?.header || '');
  }, [config?.header]);

  const handleSaveCaption = () => {
    setIsEditingCaption(false);
    if (onContentChange && config) {
      const updated: ExampleElementContent = { config: { ...config, caption: captionValue } };
      onContentChange(JSON.stringify(updated));
    }
  };

  const handleSaveHeader = () => {
    setIsEditingHeader(false);
    if (onContentChange && config) {
      const updated: ExampleElementContent = { config: { ...config, header: headerValue } };
      onContentChange(JSON.stringify(updated));
    }
  };

  // Independent zoom handlers for before image
  const handleBeforeZoomIn = onContentChange && config ? () => {
    const newZoom = Math.min(beforeZoom + 0.25, 3);
    const updated: ExampleElementContent = { config: { ...config, beforeZoom: newZoom } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  const handleBeforeZoomOut = onContentChange && config ? () => {
    const newZoom = Math.max(beforeZoom - 0.25, 0.25);
    const updated: ExampleElementContent = { config: { ...config, beforeZoom: newZoom } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  const handleBeforeZoomReset = onContentChange && config ? () => {
    const updated: ExampleElementContent = { config: { ...config, beforeZoom: DEFAULT_ZOOM, beforePanOffset: { x: 0, y: 0 } } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  const handleBeforeCenter = onContentChange && config ? () => {
    const updated: ExampleElementContent = { config: { ...config, beforePanOffset: { x: 0, y: 0 } } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  // Independent zoom handlers for after image
  const handleAfterZoomIn = onContentChange && config ? () => {
    const newZoom = Math.min(afterZoom + 0.25, 3);
    const updated: ExampleElementContent = { config: { ...config, afterZoom: newZoom } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  const handleAfterZoomOut = onContentChange && config ? () => {
    const newZoom = Math.max(afterZoom - 0.25, 0.25);
    const updated: ExampleElementContent = { config: { ...config, afterZoom: newZoom } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  const handleAfterZoomReset = onContentChange && config ? () => {
    const updated: ExampleElementContent = { config: { ...config, afterZoom: DEFAULT_ZOOM, afterPanOffset: { x: 0, y: 0 } } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  const handleAfterCenter = onContentChange && config ? () => {
    const updated: ExampleElementContent = { config: { ...config, afterPanOffset: { x: 0, y: 0 } } };
    onContentChange(JSON.stringify(updated));
  } : undefined;

  if (!config) return null;

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
      {/* Editable header */}
      <div className="mb-4">
        {isEditingHeader ? (
          <input
            type="text"
            value={headerValue}
            onChange={(e) => setHeaderValue(e.target.value)}
            onBlur={handleSaveHeader}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveHeader();
              } else if (e.key === 'Escape') {
                setHeaderValue(config?.header || '');
                setIsEditingHeader(false);
              }
            }}
            className="text-xl font-semibold text-gray-900 w-full bg-transparent border-b-2 border-blue-500 outline-none"
            autoFocus
            placeholder="Add a header..."
          />
        ) : (
          <h3
            onClick={() => setIsEditingHeader(true)}
            className="text-xl font-semibold text-gray-900 cursor-text hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
          >
            {config.header || <span className="text-gray-400 font-normal">Click to add header...</span>}
          </h3>
        )}
      </div>

      {/* Company/Event metadata */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          {config.companyName && (
            <>
              <a href={`/${config.companySlug}`} className="font-medium hover:underline">
                {config.companyName}
              </a>
              {config.eventType && (
                <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                  {config.eventType.replace(/_/g, ' ')}
                </span>
              )}
              {config.period && <span className="text-gray-400">• {config.period}</span>}
            </>
          )}
        </div>
        {config.compareViewerUrl && (
          <a
            href={config.compareViewerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open in viewer
          </a>
        )}
      </div>

      {/* Side-by-side images with markers */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
        <ExampleImageViewer
          imageUrl={config.beforeImageUrl}
          cropCenter={effectiveBeforeCropCenter}
          marker={config.beforeMarker}
          panOffset={config.beforePanOffset}
          zoom={beforeZoom}
          label="Before"
          markerColor="bg-red-400"
          onMarkerChange={onContentChange ? (newMarker) => {
            const updated: ExampleElementContent = { config: { ...config, beforeMarker: newMarker } };
            onContentChange(JSON.stringify(updated));
          } : undefined}
          onPanChange={onContentChange ? (newPan) => {
            const updated: ExampleElementContent = { config: { ...config, beforePanOffset: newPan } };
            onContentChange(JSON.stringify(updated));
          } : undefined}
          onZoomIn={handleBeforeZoomIn}
          onZoomOut={handleBeforeZoomOut}
          onZoomReset={handleBeforeZoomReset}
          onCenter={handleBeforeCenter}
          onCropUrlChange={onContentChange ? (cropUrl) => {
            const updated: ExampleElementContent = { config: { ...config, beforeCropUrl: cropUrl } };
            onContentChange(JSON.stringify(updated));
          } : undefined}
        />
        <ExampleImageViewer
          imageUrl={config.afterImageUrl}
          cropCenter={effectiveAfterCropCenter}
          marker={config.afterMarker}
          panOffset={config.afterPanOffset}
          zoom={afterZoom}
          label="After"
          markerColor="bg-green-400"
          onMarkerChange={onContentChange ? (newMarker) => {
            const updated: ExampleElementContent = { config: { ...config, afterMarker: newMarker } };
            onContentChange(JSON.stringify(updated));
          } : undefined}
          onPanChange={onContentChange ? (newPan) => {
            const updated: ExampleElementContent = { config: { ...config, afterPanOffset: newPan } };
            onContentChange(JSON.stringify(updated));
          } : undefined}
          onZoomIn={handleAfterZoomIn}
          onZoomOut={handleAfterZoomOut}
          onZoomReset={handleAfterZoomReset}
          onCenter={handleAfterCenter}
          onCropUrlChange={onContentChange ? (cropUrl) => {
            const updated: ExampleElementContent = { config: { ...config, afterCropUrl: cropUrl } };
            onContentChange(JSON.stringify(updated));
          } : undefined}
        />
      </div>

      {/* Editable caption */}
      <div className="mt-4 pt-4 border-t">
        {isEditingCaption ? (
          <textarea
            value={captionValue}
            onChange={(e) => setCaptionValue(e.target.value)}
            onBlur={handleSaveCaption}
            onKeyDown={(e) => e.key === 'Escape' && setIsEditingCaption(false)}
            className="w-full p-2 border rounded resize-none"
            autoFocus
            rows={2}
          />
        ) : (
          <p
            onClick={() => setIsEditingCaption(true)}
            className="text-gray-700 cursor-text hover:bg-gray-50 p-2 -m-2 rounded"
          >
            {config.caption || 'Click to add caption...'}
          </p>
        )}
      </div>
    </div>
  );
};

// Helper functions for detecting missing short links in example tables
const getRowsWithoutShortLinks = (rows: ExampleTableRow[]): ExampleTableRow[] => {
  return rows.filter(row =>
    // Has eventId and period but missing sharedLinkId (can be fetched and regenerated)
    row.eventId && row.period && !row.sharedLinkId
  );
};

const countMissingLinks = (rows: ExampleTableRow[]): number => {
  return getRowsWithoutShortLinks(rows).length;
};

// Example Table Element Component - table of text rows
interface ExampleTableElementProps {
  elementId: string;
  content: string;
  onContentChange?: (content: string) => void;
  onGenerateLinks?: (elementId: string, tableName: string) => void;
}

const ExampleTableElement: React.FC<ExampleTableElementProps> = ({ elementId, content, onContentChange, onGenerateLinks }) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [editTableExampleModal, setEditTableExampleModal] = useState<{
    open: boolean;
    row: ExampleTableRow | null;
  }>({ open: false, row: null });

  const tableContent: ExampleTableElementContent | null = useMemo(() => {
    try {
      if (!content) return null;
      return JSON.parse(content) as ExampleTableElementContent;
    } catch {
      return null;
    }
  }, [content]);

  const config = tableContent?.config;
  const missingLinksCount = config ? countMissingLinks(config.rows) : 0;

  // Sync title value with config
  useEffect(() => {
    setTitleValue(config?.title || '');
  }, [config?.title]);

  const handleSaveTitle = () => {
    setIsEditingTitle(false);
    if (onContentChange && config) {
      const updated: ExampleTableElementContent = { config: { ...config, title: titleValue || 'Examples' } };
      onContentChange(JSON.stringify(updated));
    }
  };

  const handleStartEditDescription = (row: ExampleTableRow) => {
    setEditingRowId(row.id);
    setEditingDescription(row.description);
  };

  const handleSaveDescription = () => {
    if (!editingRowId || !onContentChange || !config) return;
    const updatedRows = config.rows.map(row =>
      row.id === editingRowId ? { ...row, description: editingDescription } : row
    );
    const updated: ExampleTableElementContent = { config: { ...config, rows: updatedRows } };
    onContentChange(JSON.stringify(updated));
    setEditingRowId(null);
  };

  const handleDeleteRow = (rowId: string) => {
    if (!onContentChange || !config) return;
    const updatedRows = config.rows.filter(row => row.id !== rowId);
    const updated: ExampleTableElementContent = { config: { ...config, rows: updatedRows } };
    onContentChange(JSON.stringify(updated));
  };

  const handleMoveRow = (rowId: string, direction: 'up' | 'down') => {
    if (!onContentChange || !config) return;
    const currentIndex = config.rows.findIndex(row => row.id === rowId);
    if (currentIndex === -1) return;
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= config.rows.length) return;

    const updatedRows = [...config.rows];
    [updatedRows[currentIndex], updatedRows[newIndex]] = [updatedRows[newIndex], updatedRows[currentIndex]];
    const updated: ExampleTableElementContent = { config: { ...config, rows: updatedRows } };
    onContentChange(JSON.stringify(updated));
  };

  const handleEditAnnotations = (row: ExampleTableRow) => {
    setEditTableExampleModal({ open: true, row });
  };

  const handleSaveEditedRow = (updatedRow: ExampleTableRow) => {
    if (!onContentChange || !config) return;
    const updatedRows = config.rows.map(r =>
      r.id === updatedRow.id ? updatedRow : r
    );
    const updated: ExampleTableElementContent = { config: { ...config, rows: updatedRows } };
    onContentChange(JSON.stringify(updated));
    setEditTableExampleModal({ open: false, row: null });
  };

  const formatEventType = (eventType: string) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (!config) return null;

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
      {/* Header with title, badge, and Generate Links button */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          {isEditingTitle ? (
            <input
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleSaveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') setIsEditingTitle(false);
              }}
              className="text-lg font-semibold text-gray-900 bg-transparent border-b-2 border-blue-500 outline-none w-full"
              autoFocus
            />
          ) : (
            <>
              <h3
                onClick={() => onContentChange && setIsEditingTitle(true)}
                className={`text-lg font-semibold text-gray-900 ${onContentChange ? 'cursor-text hover:bg-gray-50 rounded px-2 -mx-2 py-1 -my-1' : ''}`}
              >
                {config.title || 'Examples'}
              </h3>

              {/* Show badge if links are missing */}
              {missingLinksCount > 0 && (
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                  {missingLinksCount} missing {missingLinksCount === 1 ? 'link' : 'links'}
                </Badge>
              )}
            </>
          )}
        </div>

        {/* Generate Links button - only show if links are missing and we can edit */}
        {onContentChange && missingLinksCount > 0 && onGenerateLinks && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onGenerateLinks(elementId, config.title || 'Examples')}
            className="flex items-center gap-2 ml-2"
          >
            <Link2 className="h-4 w-4" />
            Generate Links
          </Button>
        )}
      </div>

      {/* Table */}
      {config.rows.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No examples yet. Add examples from the graph drilldown modal.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 pr-3 font-medium w-10"></th>
                <th className="pb-2 pr-4 font-medium w-24">Quarter</th>
                <th className="pb-2 pr-4 font-medium w-36">Company</th>
                <th className="pb-2 pr-4 font-medium w-36">Change Type</th>
                <th className="pb-2 pr-4 font-medium">Description</th>
                <th className="pb-2 pr-4 font-medium w-16 text-center">View</th>
                {onContentChange && <th className="pb-2 font-medium w-24 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {config.rows.map((row, index) => (
                <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="py-3 pr-3">
                    {row.icon_url && (
                      <img
                        src={row.icon_url}
                        alt={formatEventType(row.eventType)}
                        className="w-6 h-6 object-contain"
                        title={formatEventType(row.eventType)}
                      />
                    )}
                  </td>
                  <td className="py-3 pr-4 text-gray-600">{row.period}</td>
                  <td className="py-3 pr-4">
                    {row.companySlug ? (
                      <a href={`/${row.companySlug}`} className="text-blue-600 hover:underline">
                        {row.companyName}
                      </a>
                    ) : (
                      <span className="text-gray-900">{row.companyName}</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                      {formatEventType(row.eventType)}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    {editingRowId === row.id ? (
                      <input
                        type="text"
                        value={editingDescription}
                        onChange={(e) => setEditingDescription(e.target.value)}
                        onBlur={handleSaveDescription}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveDescription();
                          if (e.key === 'Escape') setEditingRowId(null);
                        }}
                        className="w-full px-2 py-1 border rounded text-sm"
                        autoFocus
                      />
                    ) : (
                      <span
                        onClick={() => onContentChange && handleStartEditDescription(row)}
                        className={`${onContentChange ? 'cursor-text hover:bg-gray-100 px-2 py-1 -mx-2 -my-1 rounded' : ''}`}
                      >
                        {row.description || <span className="text-gray-400 italic">No description</span>}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-center">
                    {row.compareViewerUrl ? (
                      <div className="flex items-center justify-center gap-2">
                        <a
                          href={row.compareViewerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-xs"
                        >
                          View →
                        </a>
                        {row.sharedLinkId && onContentChange && (
                          <button
                            onClick={() => handleEditAnnotations(row)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Edit marker positions"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  {onContentChange && (
                    <td className="py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleMoveRow(row.id, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ChevronLeft className="w-4 h-4 rotate-90" />
                        </button>
                        <button
                          onClick={() => handleMoveRow(row.id, 'down')}
                          disabled={index === config.rows.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ChevronRight className="w-4 h-4 rotate-90" />
                        </button>
                        <button
                          onClick={() => handleDeleteRow(row.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Table Example Modal */}
      {editTableExampleModal.row && (
        <EditTableExampleModal
          open={editTableExampleModal.open}
          onOpenChange={(open) => setEditTableExampleModal({ open, row: null })}
          row={editTableExampleModal.row}
          onSave={handleSaveEditedRow}
        />
      )}
    </div>
  );
};

// Table option for picker
interface TableOption {
  id: string;
  title: string;
  rowCount: number;
}

// Graph Element Component
interface GraphElementProps {
  elementId: string;
  content: string;
  onEdit: () => void;
  setDefinition: SetDefinition;
  onContentChange?: (content: string) => void;
  onCreateExample?: (data: ExampleConfig) => void;
  onAddToTable?: (row: Omit<ExampleTableRow, 'id'>, tableId?: string) => void;
  availableTables?: TableOption[];
  lastSelectedTableId?: string | null;
  onTableSelected?: (tableId: string | null) => void;
}

const GraphElement: React.FC<GraphElementProps> = ({ elementId, content, onEdit, setDefinition, onContentChange, onCreateExample, onAddToTable, availableTables, lastSelectedTableId, onTableSelected }) => {
  const graphContent = useMemo(() => {
    try {
      if (!content || content === '') return null;
      return JSON.parse(content) as GraphElementContent;
    } catch (error) {
      console.error('Failed to parse graph content:', error);
      return null;
    }
  }, [content]);

  // Detect mode
  const isHistoricalMode = graphContent?.config?.mode === 'historical';
  const isDiffsMode = graphContent?.config?.mode === 'diffs';

  // Build request for current mode
  const currentModeRequest = useMemo(() => {
    if (!graphContent?.config || isHistoricalMode || isDiffsMode) return null;

    const apiRequest = setDefinitionToAnalyticsRequest(setDefinition);
    const recentQuarters = getRecentQuarters(1);

    // Handle backward compatibility: old configs had breakdown_by array
    let breakdown = graphContent.config.breakdown;
    if (!breakdown && (graphContent.config as any).breakdown_by) {
      breakdown = (graphContent.config as any).breakdown_by[0] || 'category';
    }

    return {
      metric: 'company_count' as const,
      periods: recentQuarters,
      options: {
        include_breakdowns: true,
        breakdown_by: [breakdown || 'category']
      },
      // Include filters from setDefinition
      ...(apiRequest.filters && Object.keys(apiRequest.filters).length > 0 && {
        filters: apiRequest.filters
      })
    };
  }, [graphContent, setDefinition, isHistoricalMode, isDiffsMode]);

  // Build request for historical mode
  const historicalModeRequest = useMemo(() => {
    if (!graphContent?.config || !isHistoricalMode) return null;

    const apiRequest = setDefinitionToAnalyticsRequest(setDefinition);

    return {
      metric: graphContent.config.metric || 'average_addons',
      periods: graphContent.config.selectedPeriods || [],
      // Include filters from setDefinition
      ...(apiRequest.filters && Object.keys(apiRequest.filters).length > 0 && {
        filters: apiRequest.filters
      })
    };
  }, [graphContent, setDefinition, isHistoricalMode]);

  // Build request for diffs mode
  const diffsModeRequest = useMemo((): DiffsAnalyticsRequest | null => {
    if (!graphContent?.config || !isDiffsMode) return null;

    const breakdown = graphContent.config.diffsBreakdown || 'event_type';
    const requireAllPeriods = graphContent.config.diffsRequireAllPeriods !== false; // Default true
    const countMode = graphContent.config.diffsCountMode || 'unique'; // Default unique

    return {
      periods: graphContent.config.diffsSelectedPeriods || [],
      options: {
        include_breakdowns: true,
        breakdown_by: [breakdown],
        require_all_periods: requireAllPeriods,
        count_mode: countMode
      }
    };
  }, [graphContent, isDiffsMode]);

  // Crosstab request for categories chart mode (used for AI context)
  const crosstabRequest = useMemo((): DiffsCrosstabRequest | null => {
    if (!graphContent?.config || !isDiffsMode) return null;
    if (graphContent.config.diffsChartMode !== 'categories') return null;
    if (!graphContent.config.diffsSelectedPeriods?.length) return null;

    return {
      periods: graphContent.config.diffsSelectedPeriods,
      group_by: 'category',
      options: { limit: 20 }
    };
  }, [graphContent, isDiffsMode]);

  // Conditional hook calls
  const { data: currentData, isLoading: isLoadingCurrent, error: errorCurrent, refetch: refetchCurrent } = useAnalytics(currentModeRequest);
  const { data: historicalData, isLoading: isLoadingHistorical, error: errorHistorical, refetch: refetchHistorical } = useHistoricalAnalytics(historicalModeRequest);
  const { data: diffsData, isLoading: isLoadingDiffs, error: errorDiffs, refetch: refetchDiffs } = useDiffsAnalytics(diffsModeRequest);
  const { data: crosstabData } = useDiffsCrosstab(crosstabRequest);

  // Select appropriate data based on mode
  const data = isDiffsMode ? diffsData : isHistoricalMode ? historicalData : currentData;
  const isLoading = isDiffsMode ? isLoadingDiffs : isHistoricalMode ? isLoadingHistorical : isLoadingCurrent;
  const error = isDiffsMode ? errorDiffs : isHistoricalMode ? errorHistorical : errorCurrent;
  const refetch = isDiffsMode ? refetchDiffs : isHistoricalMode ? refetchHistorical : refetchCurrent;

  // Listen for refresh events from the wrapper's refresh button
  useEffect(() => {
    const handleRefresh = () => {
      refetch();
    };

    window.addEventListener(`refresh-graph-${elementId}`, handleRefresh);
    return () => {
      window.removeEventListener(`refresh-graph-${elementId}`, handleRefresh);
    };
  }, [elementId, refetch]);

  // Register element context for AI writing feature
  const contextData = useMemo(() => {
    if (!graphContent?.config || !data) return null;

    const config = graphContent.config;
    const title = config.title || 'Graph';
    const chartType = config.chartType || 'bar';
    const mode = config.mode || 'current';

    // Build description and context based on mode
    let description = '';
    let contextObj: any = {
      type: 'graph',
      title,
      mode,
      chartType,
    };

    if (mode === 'diffs') {
      // Diffs mode has: overall, by_category, by_employees, by_event_type
      const periods = config.diffsSelectedPeriods || [];
      const eventCategoryFilter = config.diffsEventCategory || 'all';
      const countMode = config.diffsCountMode || 'unique'; // 'unique' = unique pages per period, 'all' = all events
      const diffsBreakdown = config.diffsBreakdown || 'event_type';

      // Filter by_event_type based on selected event category
      let filteredByEventType = data.by_event_type || {};
      if (eventCategoryFilter !== 'all' && data.by_event_type) {
        filteredByEventType = {};
        for (const [eventType, eventData] of Object.entries(data.by_event_type)) {
          if (EVENT_TYPE_CATEGORIES[eventType] === eventCategoryFilter) {
            filteredByEventType[eventType] = eventData;
          }
        }
      }

      // Calculate totals based on breakdown type (match GraphRenderer logic)
      let totalEvents = 0;
      let totalDiffs = Object.values(data.overall || {}).reduce((sum: number, p: any) => sum + (p.total_diffs || 0), 0);

      // Get the relevant breakdown data source based on diffsBreakdown
      let breakdownData: Record<string, any> = {};
      if (diffsBreakdown === 'category') {
        breakdownData = data.by_category || {};
      } else if (diffsBreakdown === 'employees') {
        breakdownData = data.by_employees || {};
      } else if (diffsBreakdown === 'event_type') {
        breakdownData = eventCategoryFilter !== 'all' ? filteredByEventType : (data.by_event_type || {});
      } else if (diffsBreakdown === 'event_category') {
        breakdownData = data.by_event_category || {};
      }

      // Sum events from the breakdown data (same logic as GraphRenderer)
      for (const breakdownValue of Object.values(breakdownData) as any[]) {
        for (const periodData of Object.values(breakdownValue || {}) as any[]) {
          totalEvents += periodData?.count || 0;
        }
      }

      // Description indicates whether counts are unique pages or all events
      const countLabel = countMode === 'unique' ? 'unique pages with events' : 'total events';
      description = `${totalEvents} ${countLabel} from ${totalDiffs} diffs across ${periods.length} periods`;

      contextObj.periods = periods;
      contextObj.countMode = countMode; // Include so AI knows data represents unique pages vs all events
      contextObj.diffsBreakdown = diffsBreakdown;

      // For categories chart mode, include crosstab table data (more useful for AI summaries)
      if (config.diffsChartMode === 'categories' && crosstabData?.data) {
        // Filter event types based on eventCategoryFilter (match GraphRenderer logic)
        const filteredEventTypes = eventCategoryFilter === 'all'
          ? ALL_EVENT_TYPES
          : ALL_EVENT_TYPES.filter(et => EVENT_TYPE_CATEGORIES[et] === eventCategoryFilter);

        // Build a TSV-style table for easier AI parsing
        const headers = ['Category', ...filteredEventTypes.map(e => e.replace(/_/g, ' ')), 'Total'];
        const rows = crosstabData.data.map(item => {
          const categoryName = item.category_name ?? item.category;
          // Calculate filtered total (sum only filtered event types)
          let filteredTotal = 0;
          filteredEventTypes.forEach(eventType => {
            filteredTotal += item.event_counts[eventType as keyof typeof item.event_counts] || 0;
          });
          const values = filteredEventTypes.map(et => item.event_counts[et as keyof typeof item.event_counts] || 0);
          return [categoryName, ...values, filteredTotal];
        })
          .filter(row => row[row.length - 1] > 0) // Only show categories with data after filtering
          .sort((a, b) => (b[b.length - 1] as number) - (a[a.length - 1] as number)); // Sort by total descending

        const grandTotal = rows.reduce((sum, row) => sum + (row[row.length - 1] as number), 0);

        contextObj.crosstab_table = [headers, ...rows].map(row => row.join('\t')).join('\n');
        contextObj.crosstab_summary = {
          total_categories: rows.length,
          total_events: grandTotal,
          total_companies: crosstabData.totals?.companies || 0,
          periods_analyzed: crosstabData.periods?.length || 0,
          event_filter: eventCategoryFilter !== 'all' ? eventCategoryFilter : undefined
        };
      } else {
        // Include raw breakdown data for other chart modes
        contextObj.overall = data.overall || {};
        contextObj.by_category = data.by_category || {};
        contextObj.by_employees = data.by_employees || {};
        contextObj.by_event_type = filteredByEventType;
      }

      if (eventCategoryFilter !== 'all') {
        contextObj.eventCategoryFilter = eventCategoryFilter;
      }
    } else if (mode === 'historical') {
      const periods = config.selectedPeriods || [];
      const periodCount = Object.keys(data.data || {}).length;
      description = `Historical data across ${periodCount} periods`;

      contextObj.periods = periods;
      contextObj.data = data.data || {};
      contextObj.summary = data.summary || {};
    } else {
      // Current mode
      description = `${data.summary?.total_companies || 0} companies`;

      contextObj.periods = ['latest'];
      contextObj.data = data.data || data.breakdowns || {};
      contextObj.summary = data.summary || {};
    }

    const contextText = JSON.stringify(contextObj, null, 2);

    return {
      elementType: 'graph' as const,
      label: `${title} (${chartType})`,
      description,
      contextText
    };
  }, [graphContent, data, crosstabData]);

  useRegisterElementContext(elementId, contextData);

  if (!graphContent?.config) {
    return (
      <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-400">
            <BarChart3 className="h-5 w-5" />
            <p className="text-sm">Click to configure graph</p>
          </div>
          <Button onClick={onEdit} variant="outline" size="sm">
            Configure
          </Button>
        </div>
      </div>
    );
  }

  const handleConfigChange = (newConfig: GraphConfig) => {
    if (onContentChange) {
      const newContent: GraphElementContent = { config: newConfig };
      onContentChange(JSON.stringify(newContent));
    }
  };

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
      <GraphRenderer
        config={graphContent.config}
        data={data}
        isLoading={isLoading}
        error={error ?? null}
        onConfigChange={handleConfigChange}
        onCreateExample={onCreateExample}
        onAddToTable={onAddToTable}
        availableTables={availableTables}
        lastSelectedTableId={lastSelectedTableId}
        onTableSelected={onTableSelected}
      />
    </div>
  );
};

// List Element Component
interface ListElementProps {
  elementId: string;
  content: string;
  onEdit: () => void;
}

// ListElement needs access to report's set definition
interface ListElementPropsWithSet extends ListElementProps {
  setDefinition: SetDefinition;
}

const ListElement: React.FC<ListElementPropsWithSet> = ({ elementId, content, onEdit, setDefinition }) => {
  const [offset, setOffset] = useState(0);
  const [accumulatedResults, setAccumulatedResults] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentSort, setCurrentSort] = useState<{ field: string; order: 'asc' | 'desc' } | undefined>(undefined);

  const listContent = useMemo(() => {
    try {
      if (!content || content === '') return null;
      return JSON.parse(content) as ListElementContent;
    } catch (error) {
      console.error('Failed to parse list content:', error);
      return null;
    }
  }, [content]);

  // Initialize sort from config
  useEffect(() => {
    if (listContent?.config?.sort && !currentSort) {
      setCurrentSort(listContent.config.sort);
    } else if (!listContent?.config?.sort && !currentSort && listContent?.config?.listType) {
      // Set default sort
      const defaultSort = listContent.config.listType === 'company'
        ? { field: 'page_count', order: 'desc' as const }
        : { field: 'plan_count', order: 'desc' as const };
      setCurrentSort(defaultSort);
    }
  }, [listContent?.config?.sort, listContent?.config?.listType, currentSort]);

  // Convert setDefinition to API request format
  const requestParams = useMemo(() => {
    const apiRequest = setDefinitionToAnalyticsRequest(setDefinition);

    const params: any = {
      period: apiRequest.period,
      periods: apiRequest.periods,
      sort: currentSort
    };

    // Only include filters if they exist
    if (apiRequest.filters && Object.keys(apiRequest.filters).length > 0) {
      params.filters = apiRequest.filters;
    }

    return params;
  }, [setDefinition, currentSort]);

  // Use different endpoints based on list type
  const isCompanyList = listContent?.config?.listType === 'company';

  const { data: companyData, isLoading: isLoadingCompanies, error: companyError, refetch: refetchCompanies } = useAnalyticsCompanies(
    isCompanyList && listContent?.config ? requestParams : null,
    offset
  );

  const { data: pageData, isLoading: isLoadingPages, error: pageError, refetch: refetchPages } = useAnalyticsPages(
    !isCompanyList && listContent?.config ? requestParams : null,
    offset
  );

  // Select the appropriate data based on list type
  const data = isCompanyList ? companyData : pageData;
  const isLoading = isCompanyList ? isLoadingCompanies : isLoadingPages;
  const error = isCompanyList ? companyError : pageError;
  const refetch = isCompanyList ? refetchCompanies : refetchPages;

  // Accumulate results when new data arrives
  useEffect(() => {
    if (data?.results) {
      if (offset === 0) {
        // First load or refresh - replace all results
        setAccumulatedResults(data.results);
        setIsLoadingMore(false);
      } else {
        // Load more - append new results
        setAccumulatedResults(prev => [...prev, ...data.results]);
        setIsLoadingMore(false);
      }
    }
  }, [data, offset]);

  // Reset accumulated results when config, sort, or filters change
  useEffect(() => {
    setOffset(0);
    setAccumulatedResults([]);
  }, [content, currentSort, setDefinition]);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    setOffset(o => o + 100);
  };

  const handleRefresh = () => {
    setOffset(0);
    setAccumulatedResults([]);
    refetch();
  };

  const handleSortChange = (field: string) => {
    setCurrentSort(prevSort => {
      // If clicking the same field, toggle order
      if (prevSort?.field === field) {
        return {
          field,
          order: prevSort.order === 'asc' ? 'desc' : 'asc'
        };
      }
      // If clicking a new field, default to descending for numbers and booleans, ascending for text
      const isNumericOrBooleanField = [
        'page_count', 'total_plans', 'total_addons', 'avg_plans_per_page',
        'addon_count', 'plan_count', 'modality_count', 'threshold_count',
        'has_freemium', 'has_trial', 'has_public_pricing', 'has_contact_pricing',
        'has_license', 'has_usage'
      ].includes(field);
      return {
        field,
        order: isNumericOrBooleanField ? 'desc' : 'asc'
      };
    });
    // Reset pagination when sorting changes
    setOffset(0);
    setAccumulatedResults([]);
  };

  // Register element context for AI writing feature
  const contextData = useMemo(() => {
    if (!listContent?.config || !data?.pagination) return null;

    const listType = listContent.config.listType || 'company';
    const total = data.pagination.total || 0;

    // Build description
    const description = `${total} ${listType === 'company' ? 'companies' : 'pages'} listed`;

    // Get top items for context (first 10)
    const topItems = accumulatedResults.slice(0, 10).map((item: any) => {
      if (listType === 'company') {
        return { name: item.company_name || item.name, page_count: item.page_count };
      } else {
        return { company: item.company_name, page: item.page_name, plan_count: item.plan_count };
      }
    });

    // Serialize data for context
    const contextText = JSON.stringify({
      type: 'list',
      listType,
      total,
      topItems,
      sort: currentSort
    }, null, 2);

    return {
      elementType: 'list' as const,
      label: `${listType === 'company' ? 'Companies' : 'Pages'} List (${total} items)`,
      description,
      contextText
    };
  }, [listContent, data, accumulatedResults, currentSort]);

  useRegisterElementContext(elementId, contextData);

  if (!listContent?.config) {
    return (
      <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-400">
            <List className="h-5 w-5" />
            <p className="text-sm">Click to configure list</p>
          </div>
          <Button onClick={onEdit} variant="outline" size="sm">
            Configure
          </Button>
        </div>
      </div>
    );
  }

  // Create merged data object with accumulated results
  const mergedData = data ? {
    ...data,
    results: accumulatedResults,
  } : undefined;

  // Get the total from pagination (both endpoints return this)
  const actualTotal = data?.pagination.total;

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
      <ListRenderer
        config={listContent.config}
        data={mergedData}
        isLoading={isLoading && offset === 0}
        error={error ?? null}
        onRefresh={handleRefresh}
        onLoadMore={data?.pagination.has_more ? handleLoadMore : undefined}
        isLoadingMore={isLoadingMore}
        actualTotal={actualTotal}
        currentSort={currentSort}
        onSortChange={handleSortChange}
      />
    </div>
  );
};

// Tile Row Element Component
interface TileRowElementProps {
  elementId: string;
  content: string;
  onEdit: () => void;
}

// TileRowElement needs access to report's set definition
interface TileRowElementPropsWithSet extends TileRowElementProps {
  setDefinition: SetDefinition;
  latestQuarter: string;
}

const TileRowElement: React.FC<TileRowElementPropsWithSet> = ({ elementId, content, onEdit, setDefinition, latestQuarter }) => {
  const tileContent = useMemo(() => {
    try {
      if (!content || content === '') return null;
      return JSON.parse(content) as TileRowElementContent;
    } catch (error) {
      console.error('Failed to parse tile content:', error);
      return null;
    }
  }, [content]);

  // Use the new summary API instead of the list API
  const { data, isLoading, error, refetch } = useAnalyticsSummary(
    tileContent?.config ? setDefinitionToAnalyticsRequest(setDefinition) : null
  );

  // Listen for refresh events from the wrapper's refresh button
  useEffect(() => {
    const handleRefresh = () => {
      refetch();
    };

    window.addEventListener(`refresh-tiles-${elementId}`, handleRefresh);
    return () => {
      window.removeEventListener(`refresh-tiles-${elementId}`, handleRefresh);
    };
  }, [elementId, refetch]);

  // Register element context for AI writing feature
  const contextData = useMemo(() => {
    if (!tileContent?.config || !data) return null;

    const tileCount = tileContent.config.tiles?.length || 0;
    const metrics = tileContent.config.tiles?.map(t => t.metric).join(', ') || '';

    // Build description from actual data
    const description = `${tileCount} metrics: ${data.total_companies || 0} companies, ${data.total_pages || 0} pages`;

    // Serialize data for context
    const contextText = JSON.stringify({
      type: 'tiles',
      metrics: tileContent.config.tiles,
      data: {
        total_companies: data.total_companies,
        total_pages: data.total_pages,
        avg_plans_per_page: data.avg_plans_per_page,
        avg_addons_per_page: data.avg_addons_per_page,
        freemium_percentage: data.freemium_percentage,
        trial_percentage: data.trial_percentage
      }
    }, null, 2);

    return {
      elementType: 'tiles' as const,
      label: `Summary Tiles (${tileCount} metrics)`,
      description,
      contextText
    };
  }, [tileContent, data]);

  useRegisterElementContext(elementId, contextData);

  if (!tileContent?.config) {
    return (
      <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-400">
            <LayoutGrid className="h-5 w-5" />
            <p className="text-sm">Click to configure tiles</p>
          </div>
          <Button onClick={onEdit} variant="outline" size="sm">
            Configure
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
      <TileSummaryRenderer
        config={tileContent.config}
        data={data}
        isLoading={isLoading}
        error={error ?? null}
        onRefresh={() => refetch()}
      />
    </div>
  );
};

// Tags Element Component
interface TagsElementProps {
  elementId: string;
  content: string;
  onEdit: () => void;
  setDefinition: SetDefinition;
}

const TagsElement: React.FC<TagsElementProps> = ({ elementId, content, onEdit, setDefinition }) => {
  const tagsContent = useMemo(() => {
    try {
      if (!content || content === '') return null;
      return JSON.parse(content) as TagElementContent;
    } catch (error) {
      console.error('Failed to parse tags content:', error);
      return null;
    }
  }, [content]);

  // First, fetch total pages from pages/slugs API
  const { data: pagesData, isLoading: isLoadingPages, error: pagesError } = usePagesSlugs();

  // Prepare request for tags data (only when we have total pages)
  const tagsDataRequest = useMemo(() => {
    if (!tagsContent?.config || !pagesData?.total) return null;

    return {
      tagId: tagsContent.config.tagId,
      tagName: tagsContent.config.tagName,
      periods: tagsContent.config.periods,
      totalPages: pagesData.total
    };
  }, [tagsContent, pagesData]);

  // Fetch tags data from database
  const { data, isLoading: isLoadingTags, error: tagsError } = useTagsData(tagsDataRequest);

  // Register element context for AI writing feature
  const contextData = useMemo(() => {
    if (!tagsContent?.config || !data) return null;

    const tagName = tagsContent.config.tagName || 'Tag';
    const periodCount = tagsContent.config.periods?.length || 0;

    // Build description from data
    const latestData = data.length > 0 ? data[data.length - 1] : null;
    const description = latestData
      ? `${tagName}: ${latestData.count} pages (${latestData.percentage?.toFixed(1)}%) in latest period`
      : `${tagName} analytics across ${periodCount} periods`;

    // Serialize data for context
    const contextText = JSON.stringify({
      type: 'tags',
      tagName,
      tagId: tagsContent.config.tagId,
      periods: tagsContent.config.periods,
      data: data.map((d: any) => ({
        period: d.period,
        count: d.count,
        percentage: d.percentage
      }))
    }, null, 2);

    return {
      elementType: 'tags' as const,
      label: `${tagName} Analytics`,
      description,
      contextText
    };
  }, [tagsContent, data]);

  useRegisterElementContext(elementId, contextData);

  if (!tagsContent?.config) {
    return (
      <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-400">
            <Tag className="h-5 w-5" />
            <p className="text-sm">Click to configure tags analytics</p>
          </div>
          <Button onClick={onEdit} variant="outline" size="sm">
            Configure
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-6 my-4">
      <TagsRenderer
        config={tagsContent.config}
        data={data}
        isLoading={isLoadingPages || isLoadingTags}
        error={pagesError ?? tagsError ?? null}
      />
    </div>
  );
};

// Add Element Modal
type AddableElementType = 'text' | 'graph' | 'list' | 'tiles' | 'tags' | 'section_header' | 'example' | 'example_table';

interface AddElementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddElement: (type: AddableElementType) => void;
}

const AddElementModal: React.FC<AddElementModalProps> = ({ open, onOpenChange, onAddElement }) => {
  const handleSelectType = (type: AddableElementType) => {
    onAddElement(type);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Element</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4 py-4">
          <button
            onClick={() => handleSelectType('section_header')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <Heading className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Section Header</span>
          </button>
          <button
            onClick={() => handleSelectType('text')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <Type className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Text</span>
          </button>
          <button
            onClick={() => handleSelectType('graph')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <BarChart3 className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Graph</span>
          </button>
          <button
            onClick={() => handleSelectType('list')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <List className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">List</span>
          </button>
          <button
            onClick={() => handleSelectType('tiles')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <LayoutGrid className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Tiles</span>
          </button>
          <button
            onClick={() => handleSelectType('tags')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <Tag className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Tags</span>
          </button>
          <button
            onClick={() => handleSelectType('example')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <ImageIcon className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Example</span>
          </button>
          <button
            onClick={() => handleSelectType('example_table')}
            className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <TableProperties className="h-8 w-8 text-gray-600 group-hover:text-blue-600" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Example Table</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Create default empty report
const createDefaultReport = (): Report => {
  const defaultSetDefinition: SetDefinition = {
    period: '2025Q4',
    categories: [],
    tags: [],
    employees: [],
    excludedCompanies: []
  };

  return {
    id: '1',
    set: defaultSetDefinition,
    elements: []
  };
};

const ReportsContent: React.FC = () => {
  // Access the element contexts for AI writing
  const { getElementContexts } = useReportElements();
  const { id: reportIdParam } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [chatWidth, setChatWidth] = useState(() => {
    const saved = localStorage.getItem('reportsChatWidth');
    return saved ? parseInt(saved, 10) : 384;
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('reportsChatCollapsed');
    return saved === 'true';
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);

  // Sidebar collapse state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('reportsSidebarCollapsed');
    return saved === 'true';
  });

  // Get the latest quarter for default set definition
  const latestQuarter = React.useMemo(() => getRecentQuarters()[0] || '2025Q4', []);

  // Report state - initialized with default report
  const [report, setReport] = useState<Report | null>(() => createDefaultReport());

  // Saved reports state
  const [currentReportId, setCurrentReportId] = useState<string | null>(reportIdParam || null);
  const [lastSavedState, setLastSavedState] = useState<string | null>(null);
  const [saveModal, setSaveModal] = useState<{ open: boolean; mode: SaveReportMode }>({
    open: false,
    mode: 'create',
  });

  // Fetch saved report data
  const { data: currentSavedReport, isLoading: isLoadingReport } = useSavedReport(currentReportId);
  const createMutation = useCreateSavedReport();
  const updateMutation = useUpdateSavedReport();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!report) return false;
    const currentState = JSON.stringify({ set: report.set, elements: report.elements });
    return currentState !== lastSavedState;
  }, [report, lastSavedState]);

  // Modal state
  const [showAddElementModal, setShowAddElementModal] = useState(false);

  // Graph config modal state
  const [graphConfigModal, setGraphConfigModal] = useState<{
    open: boolean;
    elementId: string | null;
    initialConfig?: GraphConfig;
  }>({ open: false, elementId: null });

  // List config modal state
  const [listConfigModal, setListConfigModal] = useState<{
    open: boolean;
    elementId: string | null;
    initialConfig?: ListConfig;
  }>({ open: false, elementId: null });

  // Tile config modal state
  const [tileConfigModal, setTileConfigModal] = useState<{
    open: boolean;
    elementId: string | null;
    initialConfig?: TileRowConfig;
  }>({ open: false, elementId: null });

  // Tags config modal state
  const [tagsConfigModal, setTagsConfigModal] = useState<{
    open: boolean;
    elementId: string | null;
    initialConfig?: TagConfig;
  }>({ open: false, elementId: null });

  // Example config modal state
  const [exampleConfigModal, setExampleConfigModal] = useState<{
    open: boolean;
    elementId: string | null;
    mode: 'example' | 'example_table';
  }>({ open: false, elementId: null, mode: 'example' });

  // Insert element modal state (for inserting at specific position)
  const [insertModal, setInsertModal] = useState<{ open: boolean; insertAtIndex: number | null }>({ open: false, insertAtIndex: null });

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    elementId: string | null;
    elementType: string | null;
  }>({ show: false, elementId: null, elementType: null });

  // Write with AI modal state
  const [writeWithAIModal, setWriteWithAIModal] = useState<{
    open: boolean;
    targetElementId: string | null;
    existingText: string | null;
  }>({ open: false, targetElementId: null, existingText: null });

  // Last selected table ID for quick add action in drilldown modal
  const [lastSelectedTableId, setLastSelectedTableId] = useState<string | null>(null);

  // Track newly created elements awaiting configuration (to delete if modal canceled)
  const [pendingElements, setPendingElements] = useState<Set<string>>(new Set());

  // Build list of available tables for the table picker
  const availableTables = useMemo((): TableOption[] => {
    if (!report?.elements) return [];
    return report.elements
      .filter((el): el is ReportElement & { type: 'example_table' } => el.type === 'example_table')
      .map(el => {
        try {
          // Filter out ghost elements (empty content)
          if (!el.content || el.content.trim() === '') return null;

          const content = JSON.parse(el.content) as ExampleTableElementContent;

          // Filter out tables with no config or no rows
          if (!content.config || !content.config.rows || content.config.rows.length === 0) {
            return null;
          }

          return {
            id: el.id,
            title: content.config.title || 'Untitled Table',
            rowCount: content.config.rows.length
          };
        } catch {
          // Filter out invalid JSON
          return null;
        }
      })
      .filter((table): table is TableOption => table !== null); // Remove nulls
  }, [report?.elements]);

  const MIN_WIDTH = 280;
  const MAX_WIDTH = 800;

  // Save to localStorage whenever width or collapse state changes
  useEffect(() => {
    localStorage.setItem('reportsChatWidth', chatWidth.toString());
  }, [chatWidth]);

  useEffect(() => {
    localStorage.setItem('reportsChatCollapsed', isCollapsed.toString());
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('reportsSidebarCollapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Component mount tracking with performance measurement
  const mountTimeRef = useRef<number>(performance.now());
  const initialReportIdRef = useRef<string | undefined>(reportIdParam);

  useEffect(() => {
    mountTimeRef.current = performance.now();
  }, []);


  // Load saved report when currentSavedReport changes
  useEffect(() => {
    if (currentSavedReport) {
      const loadedReport: Report = {
        id: currentSavedReport.id,
        set: currentSavedReport.set_definition,
        elements: currentSavedReport.elements || [],
      };
      setReport(loadedReport);
      setLastSavedState(JSON.stringify({
        set: currentSavedReport.set_definition,
        elements: currentSavedReport.elements || [],
      }));
    }
  }, [currentSavedReport]);

  // Sync URL param with currentReportId
  useEffect(() => {
    if (reportIdParam !== currentReportId) {
      setCurrentReportId(reportIdParam || null);
    }
  }, [reportIdParam, currentReportId]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setChatWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Saved reports handlers
  const handleSelectReport = useCallback((savedReport: SavedReportWithMeta) => {
    setCurrentReportId(savedReport.id);
    navigate(`/reports/${savedReport.id}`);
  }, [navigate]);

  const handleNewReport = useCallback(() => {
    setCurrentReportId(null);
    setReport(createDefaultReport());
    setLastSavedState(null);
    navigate('/reports');
  }, [navigate]);

  // Helper to check if element is a ghost (empty or invalid)
  const isGhostElement = useCallback((element: ReportElement): boolean => {
    if (!element.content || element.content.trim() === '') return true;

    // Additional checks for specific types
    if (element.type === 'example' || element.type === 'example_table') {
      try {
        const parsed = JSON.parse(element.content);
        if (!parsed || !parsed.config) return true;

        // Check if example_table has rows
        if (element.type === 'example_table') {
          return !parsed.config.rows || parsed.config.rows.length === 0;
        }
      } catch {
        return true;
      }
    }

    return false;
  }, []);

  // Clean ghost elements from report before saving
  const cleanReport = useCallback((reportToClean: Report): Report => {
    return {
      ...reportToClean,
      elements: reportToClean.elements.filter(el => !isGhostElement(el))
    };
  }, [isGhostElement]);

  const [isProcessingImages, setIsProcessingImages] = useState(false);

  const handleSaveReport = useCallback(async (
    name: string,
    description: string,
    visibility: ReportVisibility,
    mode?: SaveReportMode
  ) => {
    if (!report) return;

    const saveStartTime = performance.now();
    const effectiveMode = mode || saveModal.mode;

    // Clean ghost elements before saving
    const cleanedReport = cleanReport(report);

    // Skip image processing entirely - just save current state
    // Image processing happens on-demand when viewing examples
    const processedElements = cleanedReport.elements;

    const reportData = {
      name,
      description: description || undefined,
      visibility,
      set_definition: cleanedReport.set,
      elements: processedElements,
    };

    // Update local state immediately for instant UI feedback
    const updatedReport = { ...cleanedReport, elements: processedElements };
    setReport(updatedReport);
    setLastSavedState(JSON.stringify({ set: updatedReport.set, elements: updatedReport.elements }));
    setSaveModal({ open: false, mode: 'create' });

    if (effectiveMode === 'update' && currentReportId) {
      // Don't use the mutation at all - just call the API directly in the background
      updateSavedReport(currentReportId, reportData).then(
        (savedReport) => {
          // Manually update query cache to reflect changes in sidebar
          queryClient.setQueryData(['savedReports', currentReportId], (old: any) => ({
            ...old,
            ...savedReport,
            isOwner: old?.isOwner ?? true,
          }));

          queryClient.setQueryData(['savedReports'], (old: any[] | undefined) => {
            if (!old) return old;
            return old.map(report =>
              report.id === savedReport.id
                ? { ...report, ...savedReport }
                : report
            );
          });
        },
        (error) => {
          console.error('❌ [handleSaveReport] Save failed:', error);
          // Only show error toast if save actually fails
          toast.error(`Failed to save: ${error.message}`);
        }
      );
    } else {
      // For create, we need to wait for the ID
      createMutation.mutate(reportData, {
        onSuccess: (newReport) => {
          setCurrentReportId(newReport.id);
          navigate(`/reports/${newReport.id}`);
        },
        onError: (error) => {
          console.error('❌ [handleSaveReport] Create failed:', error);
        },
      });
    }
  }, [report, saveModal.mode, currentReportId, updateMutation, createMutation, navigate, cleanReport, queryClient]);

  const handleOpenSaveModal = useCallback((mode: SaveReportMode = 'create') => {
    // If no current report, always create
    if (!currentReportId) {
      setSaveModal({ open: true, mode: 'create' });
    } else if (!currentSavedReport?.isOwner) {
      // Can't update someone else's report, force saveAs
      setSaveModal({ open: true, mode: 'saveAs' });
    } else if (mode === 'update' && currentSavedReport) {
      // Direct save without modal for update mode
      handleSaveReport(
        currentSavedReport.name,
        currentSavedReport.description || '',
        currentSavedReport.visibility,
        'update'
      );
    } else {
      setSaveModal({ open: true, mode });
    }
  }, [currentReportId, currentSavedReport, handleSaveReport]);

  // Update set definition
  const updateSetDefinition = (set: SetDefinition) => {
    if (!report) return;
    setReport(prev => prev ? ({
      ...prev,
      set
    }) : null);
  };

  // Update element content
  const updateElement = (elementId: string, content: string) => {
    if (!report) return;
    setReport(prev => prev ? ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId ? { ...el, content } : el
      )
    }) : null);
  };

  // Add new element
  const addElement = (type: AddableElementType) => {
    if (!report) return;

    const newId = `${type}-${Date.now()}`;

    // Set default content based on type
    let defaultContent = '';
    if (type === 'tiles') {
      const defaultTileConfig: TileRowConfig = {
        tiles: [
          { metric: 'total_companies' },
          { metric: 'total_pages' }
        ]
      };
      const tileContent: TileRowElementContent = { config: defaultTileConfig };
      defaultContent = JSON.stringify(tileContent);
    } else if (type === 'section_header') {
      defaultContent = 'Section Header';
    }

    const newElement: ReportElement = {
      id: newId,
      type,
      content: defaultContent
    };
    setReport(prev => prev ? ({
      ...prev,
      elements: [...prev.elements, newElement]
    }) : null);

    // For graphs, lists, tiles, tags, and examples, open config modal immediately
    if (type === 'graph') {
      setGraphConfigModal({ open: true, elementId: newId });
    } else if (type === 'list') {
      setListConfigModal({ open: true, elementId: newId });
    } else if (type === 'tiles') {
      setTileConfigModal({ open: true, elementId: newId, initialConfig: type === 'tiles' ? { tiles: [{ metric: 'total_companies' }, { metric: 'total_pages' }] } : undefined });
    } else if (type === 'tags') {
      setTagsConfigModal({ open: true, elementId: newId });
    } else if (type === 'example') {
      // Track as pending - will be deleted if modal closed without configuring
      setPendingElements(prev => new Set(prev).add(newId));
      setExampleConfigModal({ open: true, elementId: newId, mode: 'example' });
    } else if (type === 'example_table') {
      // Track as pending - will be deleted if modal closed without configuring
      setPendingElements(prev => new Set(prev).add(newId));
      setExampleConfigModal({ open: true, elementId: newId, mode: 'example_table' });
    }
  };

  // Add element after a specific element by ID
  const addElementAfterById = (afterElementId: string, newElement: ReportElement) => {
    if (!report) return;

    setReport(prev => {
      if (!prev) return null;
      const index = prev.elements.findIndex(el => el.id === afterElementId);
      if (index === -1) {
        // If not found, append at end
        return { ...prev, elements: [...prev.elements, newElement] };
      }
      const newElements = [...prev.elements];
      newElements.splice(index + 1, 0, newElement);
      return { ...prev, elements: newElements };
    });
  };

  // Graph configuration handlers
  const handleEditGraph = (elementId: string, content: string) => {
    try {
      const graphContent = content ? JSON.parse(content) as GraphElementContent : null;
      setGraphConfigModal({
        open: true,
        elementId,
        initialConfig: graphContent?.config
      });
    } catch (error) {
      console.error('Failed to parse graph content:', error);
      setGraphConfigModal({ open: true, elementId });
    }
  };

  // List configuration handlers
  const handleEditList = (elementId: string, content: string) => {
    try {
      const listContent = content ? JSON.parse(content) as ListElementContent : null;
      setListConfigModal({
        open: true,
        elementId,
        initialConfig: listContent?.config
      });
    } catch (error) {
      console.error('Failed to parse list content:', error);
      setListConfigModal({ open: true, elementId });
    }
  };

  const handleSaveGraphConfig = (config: GraphConfig) => {
    if (!graphConfigModal.elementId) return;

    const content: GraphElementContent = { config };
    updateElement(graphConfigModal.elementId, JSON.stringify(content));
    setGraphConfigModal({ open: false, elementId: null });
  };

  const handleSaveListConfig = (config: ListConfig) => {
    if (!listConfigModal.elementId) return;

    const content: ListElementContent = { config };
    updateElement(listConfigModal.elementId, JSON.stringify(content));
    setListConfigModal({ open: false, elementId: null });
  };

  // Tile configuration handlers
  const handleEditTiles = (elementId: string, content: string) => {
    try {
      const tileContent = content ? JSON.parse(content) as TileRowElementContent : null;
      setTileConfigModal({
        open: true,
        elementId,
        initialConfig: tileContent?.config
      });
    } catch (error) {
      console.error('Failed to parse tile content:', error);
      setTileConfigModal({ open: true, elementId });
    }
  };

  const handleSaveTileConfig = (config: TileRowConfig) => {
    if (!tileConfigModal.elementId) return;

    const content: TileRowElementContent = { config };
    updateElement(tileConfigModal.elementId, JSON.stringify(content));
    setTileConfigModal({ open: false, elementId: null });
  };

  // Tags configuration handlers
  const handleEditTags = (elementId: string, content: string) => {
    try {
      const tagsContent = content ? JSON.parse(content) as TagElementContent : null;
      setTagsConfigModal({
        open: true,
        elementId,
        initialConfig: tagsContent?.config
      });
    } catch (error) {
      console.error('Failed to parse tags content:', error);
      setTagsConfigModal({ open: true, elementId });
    }
  };

  const handleSaveTagsConfig = (config: TagConfig) => {
    if (!tagsConfigModal.elementId) return;

    const content: TagElementContent = { config };
    updateElement(tagsConfigModal.elementId, JSON.stringify(content));
    setTagsConfigModal({ open: false, elementId: null });
  };

  // Example configuration handlers
  const handleCreateExample = async (config: ExampleConfig) => {
    if (!exampleConfigModal.elementId) {
      console.error('❌ [handleCreateExample] No elementId found!');
      return;
    }

    // CRITICAL: Remove from pending IMMEDIATELY to prevent race condition
    // The modal will close and trigger handleExampleModalClose, which would delete
    // the element if it's still in pendingElements. We remove it here first.
    const elementId = exampleConfigModal.elementId;
    setPendingElements(prev => {
      const next = new Set(prev);
      next.delete(elementId);
      return next;
    });

    // No pre-generation needed - crop URLs are generated on-demand when copying
    // This ensures URLs always match the current viewport dimensions for highest quality
    const content: ExampleElementContent = { config };
    updateElement(elementId, JSON.stringify(content));

    setExampleConfigModal({ open: false, elementId: null, mode: 'example' });
  };

  const handleCreateExampleTable = (rows: ExampleTableRow[]) => {
    if (!exampleConfigModal.elementId) return;

    const config: ExampleTableConfig = {
      title: 'Example Table',
      rows,
    };
    const content: ExampleTableElementContent = { config };
    updateElement(exampleConfigModal.elementId, JSON.stringify(content));

    // Remove from pending (successfully configured)
    setPendingElements(prev => {
      const next = new Set(prev);
      next.delete(exampleConfigModal.elementId!);
      return next;
    });

    setExampleConfigModal({ open: false, elementId: null, mode: 'example_table' });
  };

  // Handle example modal close - delete element if it was never configured
  const handleExampleModalClose = () => {
    const { elementId } = exampleConfigModal;

    if (elementId && pendingElements.has(elementId)) {
      // Element was created but not configured - delete it
      setReport(prev => {
        if (!prev) return null;
        return {
          ...prev,
          elements: prev.elements.filter(el => el.id !== elementId)
        };
      });
      setPendingElements(prev => {
        const next = new Set(prev);
        next.delete(elementId);
        return next;
      });
    }

    setExampleConfigModal({ open: false, elementId: null, mode: 'example' });
  };

  // Generate shortened links for existing example table rows
  const handleGenerateTableLinks = async (elementId: string, tableName: string) => {
    if (!report) return;

    const element = report.elements.find(el => el.id === elementId);
    if (!element || element.type !== 'example_table') return;

    try {
      const content = JSON.parse(element.content) as ExampleTableElementContent;
      const rowsToFix = getRowsWithoutShortLinks(content.config.rows);

      if (rowsToFix.length === 0) {
        toast.info('All rows already have shortened links');
        return;
      }

      // Show progress toast
      const progressToast = toast.loading(
        `Generating links: 0/${rowsToFix.length}...`
      );

      const updatedRows = [...content.config.rows];
      let successCount = 0;
      let errorCount = 0;

      // Process each row that needs a link
      for (let i = 0; i < rowsToFix.length; i++) {
        const row = rowsToFix[i];
        const rowIndex = updatedRows.findIndex(r => r.id === row.id);

        try {
          // Update progress
          toast.loading(
            `Generating links: ${i + 1}/${rowsToFix.length}...`,
            { id: progressToast }
          );

          // First try stored event annotation (has cloudinary_urls with original URLs)
          let annotationToUse = row.annotation;
          let eventData = null;

          if (!annotationToUse) {
            // Fetch from diffs API using period and event type
            const diffsResponse = await fetchDiffsPages({
              period: row.period,
              breakdown_type: 'event_type',
              breakdown_value: row.eventType,
              pagination: { page: 1, per_page: 200 },
              count_mode: 'all',
            });

            // Search through diffs for the event with matching eventId
            let foundEvent = null;
            let foundDiff = null;

            for (const diff of diffsResponse.diffs || []) {
              const event = diff.events.find(e => e.event_id === row.eventId);
              if (event) {
                foundEvent = event;
                foundDiff = diff;
                break;
              }
            }

            if (!foundEvent || !foundDiff) {
              throw new Error(`Could not find event ${row.eventId} in period ${row.period}`);
            }

            // Use event.annotation (has cloudinary_urls with original URLs)
            annotationToUse = foundEvent.annotation;
            eventData = foundEvent;

            // Store annotation for future use
            updatedRows[rowIndex] = {
              ...updatedRows[rowIndex],
              annotation: foundEvent.annotation,
              combined_annotation: foundDiff.combined_annotation,
            };
          }

          if (!annotationToUse) {
            throw new Error(`No annotation data available for event ${row.eventId}`);
          }

          // Build comparison data from annotation
          const comparisonData = buildComparisonDataFromEvent(
            {
              event_id: row.eventId || '',
              event_type: row.eventType,
              description: row.description,
              annotation: annotationToUse,
            },
            annotationToUse as unknown as ExternalEventAnnotations
          );

          if (!comparisonData) {
            throw new Error('Could not build comparison data from event');
          }

          // Create shortened link
          // Use row.description which already has the full caption extracted from URL
          const result = await createSharedLink({
            comparison_data: comparisonData,
            title: `${row.companyName} - ${row.description}`,
            campaign_source: 'report_builder',
            campaign_medium: tableName,
            campaign_name: currentSavedReport?.name || 'untitled_report',
          });

          if ('url' in result) {
            // Update row with new link data
            updatedRows[rowIndex] = {
              ...updatedRows[rowIndex],
              compareViewerUrl: result.url,
              sharedLinkId: result.id,
            };
            successCount++;
          } else {
            throw new Error(result.error || 'Failed to create shared link');
          }
        } catch (error) {
          console.error(`Failed to generate link for row ${row.id}:`, error);
          errorCount++;
        }
      }

      // Update element content with new rows
      const updatedContent: ExampleTableElementContent = {
        config: {
          ...content.config,
          rows: updatedRows,
        },
      };

      updateElement(elementId, JSON.stringify(updatedContent));

      // Dismiss progress and show result
      toast.dismiss(progressToast);

      if (errorCount === 0) {
        toast.success(`Generated ${successCount} shortened links`);
      } else {
        toast.warning(
          `Generated ${successCount} links, ${errorCount} failed`
        );
      }
    } catch (error) {
      console.error('Error generating table links:', error);
      toast.error('Failed to generate links');
    }
  };

  // Move element handlers
  const handleMoveUp = (index: number) => {
    if (!report || index === 0) return;
    const newElements = [...report.elements];
    [newElements[index - 1], newElements[index]] = [newElements[index], newElements[index - 1]];
    setReport(prev => prev ? ({ ...prev, elements: newElements }) : null);
  };

  const handleMoveDown = (index: number) => {
    if (!report || index >= report.elements.length - 1) return;
    const newElements = [...report.elements];
    [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
    setReport(prev => prev ? ({ ...prev, elements: newElements }) : null);
  };

  // Insert element at specific index
  const insertElementAt = (type: ReportElementType, index: number) => {
    if (!report) return;
    const newId = `${type}-${Date.now()}`;
    let defaultContent = '';
    if (type === 'text') {
      defaultContent = '';
    } else if (type === 'section_header') {
      defaultContent = 'Section Header';
    } else if (type === 'title') {
      defaultContent = 'My Report Title';
    }

    const newElement: ReportElement = {
      id: newId,
      type,
      content: defaultContent
    };

    const newElements = [...report.elements];
    newElements.splice(index, 0, newElement);
    setReport(prev => prev ? ({ ...prev, elements: newElements }) : null);
    setInsertModal({ open: false, insertAtIndex: null });

    // For graphs, lists, tiles, tags, and examples, open config modal immediately
    if (type === 'graph') {
      setGraphConfigModal({ open: true, elementId: newId });
    } else if (type === 'list') {
      setListConfigModal({ open: true, elementId: newId });
    } else if (type === 'tiles') {
      setTileConfigModal({ open: true, elementId: newId });
    } else if (type === 'tags') {
      setTagsConfigModal({ open: true, elementId: newId });
    } else if (type === 'example') {
      // Track as pending - will be deleted if modal closed without configuring
      setPendingElements(prev => new Set(prev).add(newId));
      setExampleConfigModal({ open: true, elementId: newId, mode: 'example' });
    } else if (type === 'example_table') {
      // Track as pending - will be deleted if modal closed without configuring
      setPendingElements(prev => new Set(prev).add(newId));
      setExampleConfigModal({ open: true, elementId: newId, mode: 'example_table' });
    }
  };

  // Delete handlers
  const handleDeleteClick = (elementId: string, elementType: string) => {
    setDeleteConfirmation({
      show: true,
      elementId,
      elementType
    });
  };

  const handleConfirmDelete = () => {
    if (!report) return;
    if (deleteConfirmation.elementId) {
      setReport(prev => prev ? ({
        ...prev,
        elements: prev.elements.filter(el => el.id !== deleteConfirmation.elementId)
      }) : null);
    }
    setDeleteConfirmation({ show: false, elementId: null, elementType: null });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ show: false, elementId: null, elementType: null });
  };

  // Write with AI handlers
  const handleOpenWriteWithAI = (elementId: string) => {
    // Get existing text content from the element
    const element = report?.elements.find(el => el.id === elementId);
    const existingText = element?.type === 'text' ? element.content : null;
    setWriteWithAIModal({ open: true, targetElementId: elementId, existingText });
  };

  const handleAIGenerate = (text: string) => {
    if (writeWithAIModal.targetElementId) {
      updateElement(writeWithAIModal.targetElementId, text);
    }
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] overflow-hidden">
      {/* Left Pane - Saved Reports Sidebar */}
      {!sidebarCollapsed && (
        <div className="w-64 flex-shrink-0">
          <SavedReportsSidebar
            currentReportId={currentReportId}
            onSelectReport={handleSelectReport}
            onNewReport={handleNewReport}
            onSaveReport={() => handleOpenSaveModal(currentReportId ? 'update' : 'create')}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        </div>
      )}

      {/* Sidebar Toggle Button */}
      <button
        onClick={toggleSidebar}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-r-md p-1.5 hover:bg-gray-50 shadow-sm transition-all"
        style={{ left: sidebarCollapsed ? 0 : 256 }}
        title={sidebarCollapsed ? 'Show saved reports' : 'Hide saved reports'}
      >
        {sidebarCollapsed ? (
          <PanelLeft className="h-4 w-4 text-gray-600" />
        ) : (
          <PanelLeftClose className="h-4 w-4 text-gray-600" />
        )}
      </button>

      {/* Right Pane - Report Canvas */}
      <div className="flex-1 bg-gray-50 overflow-y-auto relative">
        {/* Floating Save Button - Top Right */}
        <div className="sticky top-0 z-10 flex justify-end p-4 pointer-events-none">
          <Button
            onClick={() => handleOpenSaveModal(currentReportId ? 'update' : 'create')}
            size="sm"
            className={`pointer-events-auto shadow-md ${
              hasUnsavedChanges
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'bg-gray-400 hover:bg-gray-500'
            }`}
            disabled={(!hasUnsavedChanges && !!currentReportId) || isSaving || isProcessingImages}
          >
            {(isSaving || isProcessingImages) && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSaving || isProcessingImages ? (
              isProcessingImages ? 'Processing images...' : 'Saving...'
            ) : (
              <>
                {hasUnsavedChanges && <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />}
                {currentReportId ? (hasUnsavedChanges ? 'Save Changes' : 'Saved') : 'Save Report'}
              </>
            )}
          </Button>
        </div>

        {report && (
          <div className="mx-auto p-12 pl-24 max-w-6xl -mt-14">
            {/* Title */}
            {report.elements
              .filter(el => el.type === 'title')
              .map(element => (
                <div key={element.id} className="mb-8">
                  <TitleElement
                    content={element.content}
                    onChange={(content) => updateElement(element.id, content)}
                  />
                </div>
              ))}

            {/* Other Elements */}
            {report.elements
              .filter(el => el.type !== 'title')
              .map((element, index) => {
            const elementContent = (() => {
              switch (element.type) {
                case 'text':
                  return (
                    <TextElement
                      content={element.content}
                      onChange={(content) => updateElement(element.id, content)}
                    />
                  );
                case 'graph':
                  return (
                    <GraphElement
                      elementId={element.id}
                      content={element.content}
                      onEdit={() => handleEditGraph(element.id, element.content)}
                      setDefinition={report.set}
                      onContentChange={(newContent) => updateElement(element.id, newContent)}
                      onCreateExample={(exampleConfig) => {
                        const newElement: ReportElement = {
                          id: `example-${Date.now()}`,
                          type: 'example',
                          content: JSON.stringify({ config: exampleConfig })
                        };
                        addElementAfterById(element.id, newElement);
                        toast.success('Example added to report');
                      }}
                      onAddToTable={(rowData, tableId) => {
                        const newRow: ExampleTableRow = {
                          id: `row-${Date.now()}`,
                          ...rowData
                        };

                        if (tableId) {
                          // Add to specified table
                          const targetTable = report.elements.find(el => el.id === tableId);
                          if (targetTable?.type === 'example_table') {
                            try {
                              const existingContent = JSON.parse(targetTable.content) as ExampleTableElementContent;
                              const updatedContent: ExampleTableElementContent = {
                                config: {
                                  ...existingContent.config,
                                  rows: [...existingContent.config.rows, newRow]
                                }
                              };
                              updateElement(targetTable.id, JSON.stringify(updatedContent));
                              toast.success(`Added to "${existingContent.config.title || 'table'}"`);
                            } catch {
                              toast.error('Failed to add to table');
                            }
                          }
                        } else {
                          // Create new example_table element after graph
                          const newTableId = `example-table-${Date.now()}`;
                          const newElement: ReportElement = {
                            id: newTableId,
                            type: 'example_table',
                            content: JSON.stringify({
                              config: {
                                title: 'Examples',
                                rows: [newRow]
                              }
                            } as ExampleTableElementContent)
                          };
                          addElementAfterById(element.id, newElement);
                          setLastSelectedTableId(newTableId);
                          toast.success('Table created with example');
                        }
                      }}
                      availableTables={availableTables}
                      lastSelectedTableId={lastSelectedTableId}
                      onTableSelected={setLastSelectedTableId}
                    />
                  );
                case 'list':
                  return (
                    <ListElement
                      elementId={element.id}
                      content={element.content}
                      onEdit={() => handleEditList(element.id, element.content)}
                      setDefinition={report.set}
                    />
                  );
                case 'tiles':
                  return (
                    <TileRowElement
                      elementId={element.id}
                      content={element.content}
                      onEdit={() => handleEditTiles(element.id, element.content)}
                      setDefinition={report.set}
                      latestQuarter={latestQuarter}
                    />
                  );
                case 'tags':
                  return (
                    <TagsElement
                      elementId={element.id}
                      content={element.content}
                      onEdit={() => handleEditTags(element.id, element.content)}
                      setDefinition={report.set}
                    />
                  );
                case 'example':
                  return (
                    <ExampleElement
                      elementId={element.id}
                      content={element.content}
                      onContentChange={(newContent) => updateElement(element.id, newContent)}
                    />
                  );
                case 'example_table':
                  return (
                    <ExampleTableElement
                      elementId={element.id}
                      content={element.content}
                      onContentChange={(newContent) => updateElement(element.id, newContent)}
                      onGenerateLinks={handleGenerateTableLinks}
                    />
                  );
                case 'section_header':
                  return (
                    <SectionHeaderElement
                      content={element.content}
                      onChange={(content) => updateElement(element.id, content)}
                    />
                  );
                default:
                  return null;
              }
            })();

            // Additional actions based on element type
            let additionalActions: React.ReactNode = undefined;

            if (element.type === 'tiles' || element.type === 'graph') {
              // For tiles and graphs, add edit and refresh buttons
              additionalActions = (
                <>
                  <button
                    onClick={() => {
                      if (element.type === 'tiles') {
                        handleEditTiles(element.id, element.content);
                      } else if (element.type === 'graph') {
                        handleEditGraph(element.id, element.content);
                      }
                    }}
                    className="p-1 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600"
                    title={element.type === 'tiles' ? 'Edit tiles' : 'Edit graph'}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      const eventName = element.type === 'tiles' ? `refresh-tiles-${element.id}` : `refresh-graph-${element.id}`;
                      const event = new CustomEvent(eventName);
                      window.dispatchEvent(event);
                    }}
                    className="p-1 rounded hover:bg-green-100 text-gray-400 hover:text-green-600"
                    title="Refresh data"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </>
              );
            } else if (element.type === 'text') {
              // For text elements, add Write with AI button
              additionalActions = (
                <button
                  onClick={() => handleOpenWriteWithAI(element.id)}
                  className="p-1 rounded hover:bg-purple-100 text-gray-400 hover:text-purple-600"
                  title="Write with AI"
                >
                  <Sparkles className="h-4 w-4" />
                </button>
              );
            }

            return (
              <ElementWrapper
                key={element.id}
                id={element.id}
                index={index}
                totalElements={report.elements.filter(el => el.type !== 'title').length}
                onDelete={() => handleDeleteClick(element.id, element.type)}
                onMoveUp={() => handleMoveUp(report.elements.findIndex(el => el.id === element.id))}
                onMoveDown={() => handleMoveDown(report.elements.findIndex(el => el.id === element.id))}
                onInsertBefore={() => setInsertModal({ open: true, insertAtIndex: report.elements.findIndex(el => el.id === element.id) })}
                additionalActions={additionalActions}
              >
                {elementContent}
              </ElementWrapper>
            );
          })}

            {/* Add Element Button */}
            <div className="flex justify-center mt-8">
              <Button
                onClick={() => setShowAddElementModal(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Element
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Element Modal */}
      <AddElementModal
        open={showAddElementModal}
        onOpenChange={setShowAddElementModal}
        onAddElement={addElement}
      />

      {/* Insert Element Modal */}
      <AddElementModal
        open={insertModal.open}
        onOpenChange={(open) => !open && setInsertModal({ open: false, insertAtIndex: null })}
        onAddElement={(type) => {
          if (insertModal.insertAtIndex !== null) {
            insertElementAt(type, insertModal.insertAtIndex);
          }
          setInsertModal({ open: false, insertAtIndex: null });
        }}
      />

      {/* Graph Config Modal */}
      <GraphConfigModal
        open={graphConfigModal.open}
        onOpenChange={(open) => !open && setGraphConfigModal({ open: false, elementId: null })}
        initialConfig={graphConfigModal.initialConfig}
        onSave={handleSaveGraphConfig}
      />

      {/* List Config Modal */}
      <ListConfigModal
        open={listConfigModal.open}
        onOpenChange={(open) => !open && setListConfigModal({ open: false, elementId: null })}
        initialConfig={listConfigModal.initialConfig}
        onSave={handleSaveListConfig}
      />

      {/* Tile Config Modal */}
      <TileConfigModal
        open={tileConfigModal.open}
        onOpenChange={(open) => !open && setTileConfigModal({ open: false, elementId: null })}
        initialConfig={tileConfigModal.initialConfig}
        onSave={handleSaveTileConfig}
        setDefinition={report?.set}
      />

      {/* Tags Config Modal */}
      <TagsConfigModal
        open={tagsConfigModal.open}
        onOpenChange={(open) => !open && setTagsConfigModal({ open: false, elementId: null })}
        initialConfig={tagsConfigModal.initialConfig}
        onSave={handleSaveTagsConfig}
      />

      {/* Example Config Modal */}
      <ExampleConfigModal
        open={exampleConfigModal.open}
        onOpenChange={(open) => !open && handleExampleModalClose()}
        mode={exampleConfigModal.mode}
        onCreateExample={handleCreateExample}
        onCreateExampleTable={handleCreateExampleTable}
        reportTitle={currentSavedReport?.name}
        onAddToTable={(rowData, tableId) => {
          const newRow: ExampleTableRow = {
            id: `row-${Date.now()}`,
            ...rowData
          };

          if (tableId) {
            // Add to specified table
            const targetTable = report?.elements.find(el => el.id === tableId);
            if (targetTable?.type === 'example_table') {
              try {
                const existingContent = JSON.parse(targetTable.content) as ExampleTableElementContent;
                const updatedContent: ExampleTableElementContent = {
                  config: {
                    ...existingContent.config,
                    rows: [...existingContent.config.rows, newRow]
                  }
                };
                updateElement(targetTable.id, JSON.stringify(updatedContent));
              } catch {
                toast.error('Failed to add to table');
              }
            }
          } else {
            // Create new example_table element
            if (!exampleConfigModal.elementId) return;
            const newTableId = `example-table-${Date.now()}`;
            const newElement: ReportElement = {
              id: newTableId,
              type: 'example_table',
              content: JSON.stringify({
                config: {
                  title: 'Examples',
                  rows: [newRow]
                }
              } as ExampleTableElementContent)
            };
            addElementAfterById(exampleConfigModal.elementId, newElement);
            setLastSelectedTableId(newTableId);
          }
        }}
        availableTables={availableTables}
        lastSelectedTableId={lastSelectedTableId}
        onTableSelected={setLastSelectedTableId}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmation.show} onOpenChange={(open) => !open && handleCancelDelete()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Element</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deleteConfirmation.elementType} element? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Report Modal */}
      <SaveReportModal
        open={saveModal.open}
        onOpenChange={(open) => !open && setSaveModal({ open: false, mode: 'create' })}
        mode={saveModal.mode}
        existingReport={currentSavedReport}
        onSave={handleSaveReport}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      {/* Write with AI Modal */}
      <WriteWithAIModal
        open={writeWithAIModal.open}
        onOpenChange={(open) => !open && setWriteWithAIModal({ open: false, targetElementId: null, existingText: null })}
        onGenerate={handleAIGenerate}
        availableElements={getElementContexts()}
        existingText={writeWithAIModal.existingText || undefined}
      />
    </div>
  );
};

// Main Reports component - wraps ReportsContent with provider
export const Reports: React.FC = () => {
  return (
    <ReportElementsProvider>
      <ReportsContent />
    </ReportElementsProvider>
  );
};

export default Reports;
